import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import BoardSVG from './components/BoardSVG'
import MatchFoundScreen from './components/MatchFoundScreen'
import VideoAdModal from './components/VideoAdModal'
import OutOfGearModal from './components/OutOfGearModal'
import GameOverScreenV2 from './components/GameOverScreenV2'
import TopMenuBar, { Account } from './components/TopMenuBar'
import UsernameSelectionModal from './components/UsernameSelectionModal'
import LimitReachedModal from './components/LimitReachedModal'
import RemoveAdsModal from './components/RemoveAdsModal'
import Leaderboard from './components/Leaderboard'
import Store from './components/Store'
import AccountPage from './components/AccountPage'
import TournamentsPage from './components/TournamentsPage'
import TournamentWarningModal from './components/TournamentWarningModal'
import Contact from './components/Contact'
import DeleteAccount from './components/DeleteAccount'
import PrivacyPolicy from './components/PrivacyPolicy'
import PageLayout from './components/PageLayout'
import AchievementsPage from './components/AchievementsPage'
import AchievementsToastHost from './components/AchievementsToastHost'
import { AchievementsProvider, useAchievements } from './components/AchievementsProvider'
import { SidekickOverlay, SidekickProvider } from './components/SidekickOverlay'
import { shouldPlaySound } from './components/SoundToggle'
import { increment as incrementPlayGamesAchievement, initPlayGames, resetSignInState } from './lib/playGamesAchievements'
import { isUnityAdShowing, maybeShowAdBeforeMatchmaking, recordGameCompleted, setAdsRemoved, syncAdsRemovedFromBilling } from './lib/unityAds'
import { useRoom } from './lib/useRoom'
import { useViewport } from './hooks/useViewport'
import { ANDROID_TABLET_MENU_WIDTH } from './lib/androidLayout'

// --- Config ---
const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const API = (import.meta as any).env?.VITE_API ?? 'http://localhost:8100';
const ENABLE_ADS = (import.meta as any).env?.VITE_ENABLE_ADS === 'true';

// Storage for regular user JWT tokens (all platforms - JWT is primary auth method)
function getRegularUserToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('regular_user_jwt');
}

function setRegularUserToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem('regular_user_jwt', token);
  } else {
    localStorage.removeItem('regular_user_jwt');
  }
}

async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  // Check for guest token (but don't send it for login/register requests)
  const { getValidGuestToken } = await import('./lib/guest-auth');
  let guestToken = await getValidGuestToken();
  
  // Check for regular user JWT token (all platforms - JWT is primary)
  let regularUserToken: string | null = null;
  if (path !== "/login" && path !== "/register") {
    regularUserToken = getRegularUserToken();
  }
  
  // Don't send JWT token for login/register - they use session cookies only
  const skipJWT = path === "/login" || path === "/register";
  
  const headers: HeadersInit = { "Content-Type": "application/json", ...(opts.headers || {}) };
  // Prefer regular user token over guest token
  if (regularUserToken && !skipJWT) {
    headers["Authorization"] = `Bearer ${regularUserToken}`;
  } else if (guestToken && !skipJWT) {
    headers["Authorization"] = `Bearer ${guestToken}`;
  } else if (!skipJWT) {
    // Debug: log when no token is available (especially for iOS)
    const isIOS = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
    if (isIOS) {
      console.warn(`[API] No JWT token available for ${path}. Regular: ${regularUserToken ? 'YES' : 'NO'}, Guest: ${guestToken ? 'YES' : 'NO'}`);
      // Check what's actually in localStorage
      const stored = localStorage.getItem('regular_user_jwt');
      console.log(`[API] localStorage check: ${stored ? 'Token exists' : 'No token in localStorage'}`);
    }
  }

  const url = `${API_BASE}${path}`;
  // Debug logging for iOS
  const isIOS = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  if (isIOS) {
    // Log auth status for debugging
    if (regularUserToken && !skipJWT) {
      console.log(`[iOS] Sending regular user JWT token for: ${path}`);
    } else if (guestToken && !skipJWT) {
      console.log(`[iOS] Sending guest JWT token for: ${path}`);
    } else if (!skipJWT) {
      console.log(`[iOS] WARNING: No JWT token available for: ${path}`);
    }
    if (path === "/login" || path === "/register") {
      console.log(`[iOS] Making ${opts.method || 'GET'} request to: ${url}`);
      console.log(`[iOS] API_BASE: ${API_BASE}`);
      console.log(`[iOS] Headers:`, headers);
      if (opts.body) {
        console.log(`[iOS] Body:`, opts.body);
      }
    }
  }

  const res = await fetch(url, {
    credentials: "include", // send/receive session cookie
    headers,
    ...opts,
  });
  
  if (isIOS && (path === "/login" || path === "/register")) {
    console.log(`[iOS] Response status: ${res.status} ${res.statusText}`);
    const responseHeaders = Object.fromEntries(res.headers.entries());
    console.log(`[iOS] Response headers:`, responseHeaders);
    
    // Extract Set-Cookie header and send it to native iOS code to store
    const setCookie = res.headers.get("set-cookie");
    if (setCookie) {
      try {
        // Parse the Set-Cookie header: "name=value; path=/; Max-Age=604800; httponly; samesite=none; secure"
        const cookieParts = setCookie.split(";").map(p => p.trim());
        const nameValue = cookieParts[0];
        const equalsIndex = nameValue.indexOf("=");
        if (equalsIndex > 0) {
          const name = nameValue.substring(0, equalsIndex);
          const value = nameValue.substring(equalsIndex + 1);
          
          // Extract attributes
          let path = "/";
          let maxAge: number | undefined;
          let secure = false;
          let httpOnly = false;
          
          for (let i = 1; i < cookieParts.length; i++) {
            const part = cookieParts[i].toLowerCase();
            if (part.startsWith("path=")) {
              path = cookieParts[i].substring(5);
            } else if (part.startsWith("max-age=")) {
              maxAge = parseInt(cookieParts[i].substring(8), 10);
            } else if (part === "secure") {
              secure = true;
            } else if (part === "httponly") {
              httpOnly = true;
            }
          }
          
          // Extract domain from API_BASE
          try {
            const apiUrl = new URL(API_BASE);
            const domain = apiUrl.hostname;
            
            // Send cookie to native iOS code via window.webkit.messageHandlers
            if ((window as any).webkit && (window as any).webkit.messageHandlers && (window as any).webkit.messageHandlers.storeCookie) {
              (window as any).webkit.messageHandlers.storeCookie.postMessage({
                name: name,
                value: value,
                domain: domain,
                path: path,
                secure: secure,
                httpOnly: httpOnly,
                maxAge: maxAge
              });
              console.log(`[iOS] Sent cookie to native code: ${name}=${value.substring(0, 20)}...`);
            } else {
              console.log(`[iOS] window.webkit.messageHandlers.storeCookie not available`);
            }
          } catch (e) {
            console.error(`[iOS] Failed to parse API_BASE for cookie domain:`, e);
          }
        }
      } catch (e) {
        console.error(`[iOS] Error parsing Set-Cookie header:`, e);
      }
    }
  }

  // If we get 401 and we're using a guest token, try refreshing and retry once
  // (but skip for login/register since they don't use JWT tokens)
  if (!res.ok && res.status === 401 && guestToken && !skipJWT) {
    // Try to get a fresh token (this will trigger rotation if needed)
    const freshToken = await getValidGuestToken();
    if (freshToken && freshToken !== guestToken) {
      // Token was refreshed/rotated, retry the request
      const retryHeaders: HeadersInit = { "Content-Type": "application/json", ...(opts.headers || {}) };
      retryHeaders["Authorization"] = `Bearer ${freshToken}`;
      
      const retryRes = await fetch(`${API_BASE}${path}`, {
        credentials: "include",
        headers: retryHeaders,
        ...opts,
      });
      
      if (!retryRes.ok) throw new Error(await retryRes.text());
      return retryRes.json();
    }
  }

  if (!res.ok) {
    let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
    try {
      const errorText = await res.text();
      if (isIOS && (path === "/login" || path === "/register")) {
        console.error(`[iOS] Request failed: ${res.status} ${res.statusText}`);
        console.error(`[iOS] Error response text:`, errorText);
      }
      
      // Try to parse as JSON (FastAPI returns JSON errors like {"detail": "BAD_CREDENTIALS"})
      if (errorText) {
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorText;
        } catch {
          // Not JSON, use as-is
          errorMessage = errorText;
        }
      }
    } catch (e) {
      // Failed to read response body
      if (isIOS && (path === "/login" || path === "/register")) {
        console.error(`[iOS] Failed to read error response:`, e);
      }
    }
    
    if (isIOS && (path === "/login" || path === "/register")) {
      console.error(`[iOS] Final error message:`, errorMessage);
    }
    
    throw new Error(errorMessage);
  }
  return res.json();
}

// --- Types ---
// Account type is now exported from TopMenuBar component

// --- Reusable UI bits ---
function Card(props: { title?: string; children: React.ReactNode; footer?: React.ReactNode }) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <div className="mx-auto max-w-md shadow-xl border-4" style={{
      background: 'transparent',
      borderColor: 'transparent',
      borderRadius: isMobileLandscape ? '0.5rem' : '1rem'
    }}>
      <div style={{ padding: isMobileLandscape ? '0' : '1.5rem' }}>
        {props.title && <h2 className={`font-bold ${isMobileLandscape ? 'text-sm mb-0.5' : 'text-2xl mb-4'}`} style={{ color: '#3e2615' }}>{props.title}</h2>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '0.75rem' }}>{props.children}</div>
        {props.footer && <div className={`border-t ${isMobileLandscape ? 'mt-1 pt-1 text-xs' : 'mt-6 pt-4 text-sm'}`} style={{ borderColor: '#8b6542', color: '#3e2615' }}>{props.footer}</div>}
      </div>
    </div>
  );
}

function Input({ label, ...rest }: any) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <label className="block">
      <span className={`font-medium text-left block ${isMobileLandscape ? 'text-[11px] mb-0.5' : 'text-sm mb-1'}`} style={{ color: '#3e2615' }}>{label}</span>
      <input className={`w-full outline-none transition-all ${isMobileLandscape ? 'rounded-lg p-1 text-xs' : 'rounded-xl p-3'}`}
        style={{
          border: '2px solid #c4a07a',
          background: '#ffffff',
          color: '#3e2615',
          fontSize: '16px' // Prevents zoom on iOS
        }}
        onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
        onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
        {...rest} 
      />
    </label>
  );
}

function Button({ children, ...rest }: any) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <button className={`w-full font-bold transition-all disabled:opacity-50 ${isMobileLandscape ? 'rounded-lg py-1 text-xs' : 'rounded-xl py-3 text-lg'}`}
      style={{
        background: '#EAD8C0',
        color: '#3e2615',
        boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
        border: '2px solid #8b6542'
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
      onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      {...rest}>
      {children}
    </button>
  );
}

// --- Shared helpers ---
function computeCoverTransform(
  containerW: number,
  containerH: number,
  naturalW: number,
  naturalH: number
) {
  const scale = Math.max(containerW / naturalW, containerH / naturalH);
  const renderedW = naturalW * scale;
  const renderedH = naturalH * scale;
  const offsetX = (containerW - renderedW) / 2;
  const offsetY = (containerH - renderedH) / 2;
  return { scale, offsetX, offsetY };
}

// Helper to get correct asset path for both web and Capacitor
function getAssetPath(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.replace(/^\//, '');
  const isBrowser = typeof window !== 'undefined';

  // In Capacitor, we need to construct the path based on the actual base URL
  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === 'function'
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === 'function'
        ? cap.getPlatform() === 'ios' || cap.getPlatform() === 'android'
        : cap.platform === 'ios' || cap.platform === 'android');

  if (isNative) {
    try {
      // Get the base URL from window location
      const baseUrl = new URL(window.location.href);
      // Construct absolute path from the origin
      return baseUrl.origin + '/' + cleanPath;
    } catch (e) {
      // Fallback to absolute path (legacy Capacitor behavior)
      console.warn('[getAssetPath] Failed to construct URL, using fallback:', e);
      return '/' + cleanPath;
    }
  }

  // For web builds, always use relative paths so this works on itch.io subdirectories too.
  return './' + cleanPath;
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const resolvedPath = getAssetPath(src);
    
    // Log for debugging (especially useful in Capacitor) - DEV-only
    const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor;
    if (isCapacitor && (import.meta as any).env?.DEV) {
      console.log(`[loadImage] Capacitor detected, loading: ${src} -> ${resolvedPath}`);
    }
    
    img.onload = () => {
      if (isCapacitor && (import.meta as any).env?.DEV) {
        console.log(`[loadImage] ✓ Successfully loaded in Capacitor: ${resolvedPath}`);
      }
      resolve(img);
    };
    img.onerror = (err) => {
      console.error(`[loadImage] ✗ FAILED to load: ${src} (resolved to: ${resolvedPath})`, err);
      console.error(`[loadImage] Window location:`, window.location.href);
      console.error(`[loadImage] Base tag:`, document.querySelector('base')?.href);
      reject(err);
    };
    img.src = resolvedPath;
  });
}

function extractOpaqueRect(img: HTMLImageElement) {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[extractOpaqueRect] Failed to get 2d context');
      return null;
    }

    ctx.drawImage(img, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

    if (maxX === -1 || maxY === -1) {
      console.warn('[extractOpaqueRect] No opaque pixels found');
      return null;
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
    };
  } catch (error) {
    console.error('[extractOpaqueRect] Error extracting opaque rect:', error);
    return null;
  }
}

function extractOpaqueRects(img: HTMLImageElement) {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[extractOpaqueRects] Failed to get 2d context');
      return [];
    }

    ctx.drawImage(img, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const visited = new Uint8Array(width * height);
    const rects: Array<{ x: number; y: number; width: number; height: number }> = [];

    const dirs = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];

    const idx = (x: number, y: number) => y * width + x;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = idx(x, y);
        if (visited[i]) continue;
        const alpha = data[i * 4 + 3];
        if (alpha === 0) {
          visited[i] = 1;
          continue;
        }

        let minX = x;
        let maxX = x;
        let minY = y;
        let maxY = y;

        const queue = [i];
        visited[i] = 1;

        while (queue.length) {
          const cur = queue.pop() as number;
          const cy = Math.floor(cur / width);
          const cx = cur - cy * width;

          if (cx < minX) minX = cx;
          if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy;
          if (cy > maxY) maxY = cy;

          for (const [dx, dy] of dirs) {
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const ni = idx(nx, ny);
            if (visited[ni]) continue;
            const a = data[ni * 4 + 3];
            if (a > 0) {
              visited[ni] = 1;
              queue.push(ni);
            } else {
              visited[ni] = 1;
            }
          }
        }

        rects.push({ x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 });
      }
    }

    return rects;
  } catch (error) {
    console.error('[extractOpaqueRects] Error extracting opaque rects:', error);
    return [];
  }
}

// --- Auth context (very light) ---
function useAccount() {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try {
      // Check for guest session first
      const guestAuth = await import('./lib/guest-auth');
      const guestStorage = await import('./lib/guest-storage');
      const guestSession = await guestStorage.getGuestSession();
      

      // Check if guest user explicitly logged out (only check if there's a guest session)
      if (guestSession) {
        const isLoggedOut = await guestStorage.getLoggedOutFlag();
        if (isLoggedOut) {
          // Guest user logged out - don't auto-login, but keep session data for reuse
          setAccount(null);
          setLoading(false);
          return;
        }

        // Try to get valid token
        const token = await guestAuth.getValidGuestToken();
        if (token) {
          // Fetch account with guest token
          try {
            const data = await api<Account>("/account");
            setAccount(data);
            // Sync ads_removed status from account (Android uses Play Billing instead)
            if (data.ads_removed !== undefined) {
              const isAndroid = typeof window !== 'undefined' &&
                (window as any).Capacitor &&
                (window as any).Capacitor.getPlatform() === 'android';
              if (!isAndroid) {
                setAdsRemoved(data.ads_removed);
              }
            }
            void syncAdsRemovedFromBilling();
            setLoading(false);
            return;
          } catch (err) {
            // If account fetch fails, clear guest session and try regular account
            await guestStorage.clearGuestSession();
          }
        } else {
          // Token invalid, clear guest session
          await guestStorage.clearGuestSession();
        }
      }

      // Check if regular user explicitly logged out (sessionStorage flag)
      const regularUserLoggedOut = sessionStorage.getItem('regularUserLoggedOut') === 'true';
      if (regularUserLoggedOut) {
        // Regular user logged out - don't auto-login, clear JWT token
        setRegularUserToken(null);
        setAccount(null);
        setLoading(false);
        return;
      }
      
      // Try regular account (session cookie or JWT token)
      try {
        const data = await api<Account>("/account");
        setAccount(data);
        // Sync ads_removed status from account (Android uses Play Billing instead)
        if (data.ads_removed !== undefined) {
          const isAndroid = typeof window !== 'undefined' &&
            (window as any).Capacitor &&
            (window as any).Capacitor.getPlatform() === 'android';
          if (!isAndroid) {
            setAdsRemoved(data.ads_removed);
          }
        }
        void syncAdsRemovedFromBilling();
      } catch (err) {
        // No valid session - user is not logged in
        setAccount(null);
      }
    } catch (err) {
      // Any other error - assume not logged in
      setAccount(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void refresh(); }, []);
  return { account, setAccount, loading, refresh };
}

// --- Screens ---
function Register({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  
  // Real-time password mismatch validation
  const passwordMismatch = password && confirmPassword && password !== confirmPassword;
  const confirmPasswordBorderColor = passwordMismatch && confirmPasswordTouched ? '#d32f2f' : undefined;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setErr('Passwords do not match');
      return;
    }

    try {
      await api("/register", { method: "POST", body: JSON.stringify({ username, email, password }) });
      // Clear guest session and logged out flags when registering
      const { clearGuestSession, clearLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await clearLoggedOutFlag();
      sessionStorage.removeItem('regularUserLoggedOut');
      onSuccess();
    } catch (e: any) {
      setErr(e.message || "Registration failed");
    }
  };

  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <Card title="Create account">
      <form style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '0.75rem' }} onSubmit={submit}>
        <Input label="Username" value={username} onChange={(e: any)=>setUsername(e.target.value)} required />
        <Input label="Email" type="email" value={email} onChange={(e: any)=>setEmail(e.target.value)} required />
        <Input label="Password" type="password" value={password} onChange={(e: any)=>setPassword(e.target.value)} required />
        <label className="block">
          <span className={`font-medium text-left block ${isMobileLandscape ? 'text-[11px] mb-0.5' : 'text-sm mb-1'}`} style={{ color: '#3e2615' }}>Confirm Password</span>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmPasswordTouched(true);
              setErr(null);
            }}
            onFocus={(e) => {
              e.target.style.borderColor = passwordMismatch && confirmPasswordTouched ? '#d32f2f' : '#d4a05f';
            }}
            onBlur={(e) => {
              setConfirmPasswordTouched(true);
              e.target.style.borderColor = passwordMismatch && confirmPasswordTouched ? '#d32f2f' : '#c4a07a';
            }}
            className={`w-full outline-none transition-all ${isMobileLandscape ? 'rounded-lg p-1 text-xs' : 'rounded-xl p-3'}`}
            style={{
              border: '2px solid',
              borderColor: confirmPasswordBorderColor || '#c4a07a',
              background: '#ffffff',
              color: '#3e2615',
              fontSize: '16px' // Prevents zoom on iOS
            }}
            required
          />
          {passwordMismatch && confirmPasswordTouched && (
            <p className={`mt-1 ${isMobileLandscape ? 'text-xs' : 'text-sm'}`} style={{ color: '#d32f2f' }}>
              Passwords do not match
            </p>
          )}
        </label>
        {err && <p className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} text-red-600`}>{err}</p>}
        <Button type="submit" disabled={passwordMismatch && confirmPasswordTouched}>Sign up</Button>
      </form>
    </Card>
  );
}

function Login({ onSuccess, onGuestClick, onForgotPasswordClick }: { onSuccess: () => void; onGuestClick?: () => void; onForgotPasswordClick?: () => void }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [guestLoading, setGuestLoading] = useState(false);
  
  const handleGuestMode = async () => {
    setGuestLoading(true);
    setErr(null);
    try {
      // Close login modal and show username modal instead
      // Don't create account yet - username modal will create it when username is set
      if (onGuestClick) {
        onGuestClick();
      } else {
        onSuccess(); // Fallback: just close modal
      }
    } catch (e: any) {
      setErr(e.message || "Failed to start guest mode");
    } finally {
      setGuestLoading(false);
    }
  };
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <Card 
      title="Sign in" 
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '0.75rem' }}>
          <div className="flex justify-between">
            {onForgotPasswordClick ? (
              <button 
                onClick={onForgotPasswordClick}
                className={`underline ${isMobileLandscape ? 'text-[11px]' : 'text-sm'}`}
                style={{ color: '#3e2615', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                Forgot password?
              </button>
            ) : (
              <Link to="/forgot" className={`underline ${isMobileLandscape ? 'text-[11px]' : 'text-sm'}`}>Forgot password?</Link>
            )}
          </div>
          <div className="border-t border-gray-300" style={{ paddingTop: isMobileLandscape ? '0.25rem' : '0.75rem' }}>
            <div className={`text-center ${isMobileLandscape ? 'mb-0.5 text-[11px]' : 'mb-2 text-sm'}`} style={{ color: '#666' }}>or</div>
            <button
              onClick={handleGuestMode}
              disabled={guestLoading}
              className={`w-full px-4 rounded-lg border-2 font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${isMobileLandscape ? 'py-1 text-[11px]' : 'py-2 text-sm'}`}
              style={{
                background: 'transparent',
                color: '#3e2615',
                borderColor: '#fffaeb',
              }}
            >
              {guestLoading ? "Starting..." : "Continue as Guest"}
            </button>
          </div>
        </div>
      }
    >
      <form style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '0.75rem' }} onSubmit={async (e)=>{e.preventDefault(); setErr(null); try { const cleanedIdentifier = identifier.trim(); const cleanedPassword = password.replace(/[\r\n]+/g, ""); console.log("[Login] Attempting login for:", cleanedIdentifier); const result = await api("/login", { method: "POST", body: JSON.stringify({ identifier: cleanedIdentifier, password: cleanedPassword })}); console.log("[Login] Login successful:", result); 
        // Store JWT token if provided (all platforms - JWT is primary)
        if ((result as any).access_jwt) {
          setRegularUserToken((result as any).access_jwt);
          console.log("[Login] Stored JWT token for regular user");
          // Verify it was stored
          const stored = getRegularUserToken();
          console.log("[Login] Verified token stored:", stored ? "YES" : "NO", stored ? stored.substring(0, 20) + "..." : "");
        } else {
          console.error("[Login] WARNING: Login response did not include access_jwt token!");
          console.log("[Login] Full response:", JSON.stringify(result, null, 2));
        }
        const { clearGuestSession, clearLoggedOutFlag } = await import('./lib/guest-storage'); await clearGuestSession(); await clearLoggedOutFlag(); sessionStorage.removeItem('regularUserLoggedOut'); onSuccess(); } catch(e:any){ console.error("[Login] Login failed:", e); setErr(e.message || "Login failed"); } }}>
        <Input label="Username or email" value={identifier} onChange={(e:any)=>setIdentifier(e.target.value)} required />
        <Input label="Password" type="password" value={password} onChange={(e:any)=>setPassword(e.target.value)} required />
        {err && <p className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} text-red-600`}>{err}</p>}
        <Button type="submit">Sign in</Button>
      </form>
    </Card>
  );
}

function VerifyBanner({ emailVerified, isGuest }: { emailVerified: boolean; isGuest?: boolean }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (emailVerified || isGuest) return null;
  return (
    <div className="mb-4 rounded-xl border-4 p-4" style={{
      background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
      borderColor: '#8b6542',
      color: '#3e2615'
    }}>
      <div className="flex items-center justify-between">
        <p className="font-medium"><strong>Verify your email</strong> to unlock matchmaking & wagers. Check your inbox for the verification link.</p>
        <button disabled={sending||sent} onClick={async()=>{ setSending(true); try{ await api("/email/verify/send", { method: "POST"}); setSent(true); setTimeout(() => setSent(false), 3000);} finally{ setSending(false);} }} 
          className="rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60 transition-all"
          style={{
            background: '#EAD8C0',
            color: '#3e2615',
            border: '2px solid #8b6542',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>{sent?"Sent!":"Resend link"}</button>
      </div>
    </div>
  );
}

function ForgotPasswordForm({ onSuccess, onBack }: { onSuccess: () => void; onBack: () => void }) {
  const [identifier, setIdentifier] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  return (
    <Card title="Forgot password?">
      {done ? (
        <>
          <p className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ color: '#3e2615', marginBottom: '0.75rem' }}>
            Check your email for a reset link (valid for 30 minutes).
          </p>
          <Button onClick={onBack}>Back to Sign in</Button>
        </>
      ) : (
        <form style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '0.75rem' }} onSubmit={async (e)=>{
          e.preventDefault(); 
          setErr(null);
          try {
            await api("/password/forgot", { method: "POST", body: JSON.stringify({ identifier })}); 
            setDone(true);
          } catch (e: any) {
            setErr(e.message || "Failed to send reset link");
          }
        }}>
          <Input label="Username or email" value={identifier} onChange={(e:any)=>setIdentifier(e.target.value)} required />
          {err && <p className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} text-red-600`}>{err}</p>}
          <Button type="submit">Send reset link</Button>
          <button 
            type="button"
            onClick={onBack}
            className={`text-center underline ${isMobileLandscape ? 'text-[11px] mt-0.5' : 'text-sm'}`}
            style={{ color: '#3e2615', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            Back to Sign in
          </button>
        </form>
      )}
    </Card>
  );
}

function Forgot() {
  const [identifier, setIdentifier] = useState("");
  const [done, setDone] = useState(false);
  return (
    <Card title="Forgot password?">
      {done ? (
        <p>Check your email for a reset link (valid for 30 minutes).</p>
      ) : (
        <form className="space-y-3" onSubmit={async (e)=>{e.preventDefault(); await api("/password/forgot", { method: "POST", body: JSON.stringify({ identifier })}); setDone(true); }}>
          <Input label="Username or email" value={identifier} onChange={(e:any)=>setIdentifier(e.target.value)} required />
          <Button type="submit">Send reset link</Button>
        </form>
      )}
    </Card>
  );
}

function Reset() {
  const [params] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [done, setDone] = useState(false);
  const token = useMemo(()=> params.get("token") || "", [params]);
  return (
    <Card title="Set a new password">
      {done ? <p>Password updated. You can now <Link className="underline" to="/login">sign in</Link>.</p> : (
        <form className="space-y-3" onSubmit={async (e)=>{e.preventDefault(); await api("/password/reset", { method: "POST", body: JSON.stringify({ token, new_password: newPassword })}); setDone(true); }}>
          <Input label="New password" type="password" value={newPassword} onChange={(e:any)=>setNewPassword(e.target.value)} required />
          <Button type="submit">Update password</Button>
        </form>
      )}
    </Card>
  );
}

function Dashboard({ acct, onLogout, onEnterMine }: { acct: Account | null; onLogout: () => void; onEnterMine: () => void }) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroidTablet = viewport.isAndroidTablet;
  const isIOSPad = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase();
    const isiPadUA = ua.includes('ipad') || (ua.includes('macintosh') && (navigator as any).maxTouchPoints > 2);
    const minDim = Math.min(viewport.width, viewport.height);
    const maxDim = Math.max(viewport.width, viewport.height);
    const isPadDims = maxDim >= 768 && minDim >= 768;
    return isiPadUA && isPadDims;
  })();
  // Detect if running in browser (not Capacitor native app)
  const isBrowser = typeof window !== 'undefined' && !(window as any).Capacitor;
  
  const handleLogout = async () => {
    // Just call the parent's logout handler - it handles everything
    await onLogout();
  };
  
  // For mobile landscape on home page, use full screen layout (both logged in and logged out)
  if (isMobileLandscape) {
    return (
      <>
        {/* Full screen background image - fixed to break out of all padding */}
        <div className="fixed inset-0 w-screen h-screen overflow-hidden" style={{ margin: 0, padding: 0, zIndex: 0 }}>
          <img
            src="./EnterTheMine.png"
            alt="Goldrush Mine entrance"
            className="w-full h-full object-cover"
            draggable={false}
            style={{ width: '100vw', height: '100vh', objectFit: 'cover' }}
          />
        </div>
        <PageLayout 
          account={acct} 
          onLogout={handleLogout}
        >
          {/* Enter the mine button - centered on background image, locked at bottom */}
          <div 
            className="fixed z-20" 
            style={{ 
              left: isAndroidTablet && isMobileLandscape ? 'calc(50% - 25px)' : 'calc(50% - 20px)', // Move left 5px on Android tablets
              transform: 'translateX(-50%)',
              bottom: isAndroidTablet && isMobileLandscape 
                ? 'calc(max(calc(env(safe-area-inset-bottom, 8px) - 32px), 8px) + 48px + 40px)' // Android tablets keep lift
                : (isIOSPad ? 'calc(max(calc(env(safe-area-inset-bottom, 8px) - 32px), 8px) + 48px + 20px)' // iPad lift only
                  : (isBrowser ? 'calc(max(calc(env(safe-area-inset-bottom, 8px) - 32px), 8px) + 48px)' : 'calc(max(calc(env(safe-area-inset-bottom, 8px) - 32px), 8px) + 48px)')),
              userSelect: 'none',
              touchAction: 'none',
              pointerEvents: 'auto'
            } as React.CSSProperties}
            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
            onDrag={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
            onDragEnd={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
            draggable={false}
          >
            <button
              onClick={onEnterMine}
              draggable={false}
              onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
              onDrag={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
            onDragEnd={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
            onTouchMove={(e) => e.preventDefault()}
            className="inline-block rounded-xl px-6 py-3 font-bold text-base transition-all hover:scale-105 active:scale-95"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
              border: '2px solid #8b6542',
              userSelect: 'none',
              touchAction: 'none',
              pointerEvents: 'auto',
              ...(isAndroidTablet && isMobileLandscape ? {
                padding: '15px 30px', // Bigger padding on Android tablets
                fontSize: '23px' // Bigger font on Android tablets
              } : {}),
              ...(isIOSPad ? {
                padding: '14px 29px',
                fontSize: '20px'
              } : {})
            } as React.CSSProperties}
          >
            Enter the mine
          </button>
          </div>
        </PageLayout>
      </>
    );
  }
  
  return (
    <PageLayout 
      account={acct} 
      onLogout={handleLogout}
    >
      {/* EnterTheMine.png - full width of menu bar, close to it */}
      {/* Unverified users can play now - show "Enter the mine" button for everyone */}
      {acct ? (
        <div className="px-2 md:px-6 pb-2 md:pb-4">
          <div className="relative w-full rounded-3xl shadow overflow-hidden bg-[#f7e3c2] md:max-h-[95vh]"
               style={{ 
                 aspectRatio: '16 / 9', 
                 display: 'block',
                 maxWidth: '100%'
               }}>
            <img
              src="./EnterTheMine.png"
              alt="Goldrush Mine entrance"
              className="w-full h-full object-cover"
              draggable={false}
            />
            {/* Enter the mine button at bottom center - smaller and closer on mobile */}
            <div 
              className={isAndroidTablet ? "absolute" : "absolute bottom-2 md:bottom-6"} 
              style={{ 
                left: isAndroidTablet ? 'calc(50% - 25px)' : 'calc(50% - 20px)', // Move left 5px on Android tablets
                transform: 'translateX(-50%)',
                ...(isAndroidTablet ? { bottom: 'calc(1.5rem + 40px)' } : {}), // Original Android tablet lift
                ...(isIOSPad ? { bottom: 'calc(1.5rem + 20px)' } : {})
              }}
            >
              <button
                onClick={onEnterMine}
                className="inline-block rounded-xl px-4 py-2 md:px-8 md:py-4 font-bold text-sm md:text-lg transition-all hover:scale-105 active:scale-95"
                style={{
                  background: '#fffaeb',
                  color: '#3e2615',
                  boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                  border: '2px solid #8b6542',
                  ...(isAndroidTablet ? {
                    padding: '15px 29px', // Bigger padding on Android tablets
                    fontSize: '23px' // Bigger font on Android tablets
                  } : {}),
                  ...(isIOSPad ? {
                    padding: '10px 20px',
                    fontSize: '1.2rem'
                  } : {})
                }}
              >
                Enter the mine
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Not logged in - show dashboard with enter mine button
        <div className="px-2 md:px-6 pb-2 md:pb-4">
          <div className="relative w-full rounded-3xl shadow overflow-hidden bg-[#f7e3c2] md:max-h-[95vh]"
               style={{
                 aspectRatio: '16 / 9',
                 display: 'block',
                 maxWidth: '100%'
               }}>
            <img
              src="./EnterTheMine.png"
              alt="Goldrush Mine entrance"
              className="w-full h-full object-cover"
              draggable={false}
            />
            {/* Enter the mine button at bottom center - smaller and closer on mobile */}
            <div 
              className={isAndroidTablet ? "absolute" : "absolute bottom-2 md:bottom-6"} 
              style={{ 
                left: isAndroidTablet ? 'calc(50% - 25px)' : 'calc(50% - 20px)', // Move left 5px on Android tablets
                transform: 'translateX(-50%)',
                ...(isAndroidTablet ? { bottom: 'calc(1.5rem + 40px)' } : {}), // Original Android tablet lift
                ...(isIOSPad ? { bottom: 'calc(1.5rem + 20px)' } : {})
              }}
            >
              <button
                onClick={onEnterMine}
                className="inline-block rounded-xl px-4 py-2 md:px-8 md:py-4 font-bold text-sm md:text-lg transition-all hover:scale-105 active:scale-95"
                style={{
                  background: '#fffaeb',
                  color: '#3e2615',
                  boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                  border: '2px solid #8b6542',
                  ...(isAndroidTablet ? {
                    padding: '15px 29px', // Bigger padding on Android tablets
                    fontSize: '23px' // Bigger font on Android tablets
                  } : {}),
                  ...(isIOSPad ? {
                    padding: '10px 20px',
                    fontSize: '1.2rem'
                  } : {})
                }}
              >
                Enter the mine
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

function Gate() {
  const { account, loading, refresh, setAccount } = useAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isMobile = viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [showOutOfGearModal, setShowOutOfGearModal] = useState(false);
  const [showCompleteRegistrationModal, setShowCompleteRegistrationModal] = useState(false);
  const [pendingPlayNavigation, setPendingPlayNavigation] = useState(false);
  const location = useLocation();

  // Listen for account updates from other pages
  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  // Listen for showLoginModal event (from Store link for guests)
  useEffect(() => {
    const handleShowLoginModal = () => {
      setAuthMode('register'); // Show register form instead of login
      setShowAuthModal(true);
    };
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, []);

  // Check if we should show register modal on mount (from navigation state)
  useEffect(() => {
    if (location.state?.showRegisterModal) {
      setAuthMode('register');
      setShowAuthModal(true);
      // Clear the state so it doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Check if we should show complete registration modal on mount (from navigation state)
  useEffect(() => {
    if (location.state?.showCompleteRegistrationModal && account && !account.email_verified) {
      setShowCompleteRegistrationModal(true);
      // Clear the state so it doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, account]);

  // Show success message if email was just verified
  const verified = searchParams.get('verified') === 'true';

  // Check if we should show out of gear modal on mount (from navigation state)
  useEffect(() => {
    if (location.state?.showOutOfGearModal) {
      setShowOutOfGearModal(true);
      // Clear the state so it doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Refresh account when tab becomes visible (handles email verification case)
  // When user verifies email in another tab and returns, this refreshes the account state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - refresh account to check for verification status
        void refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  // Don't show username modal on home page - it will show on /play page
  // Don't auto-show limit reached modal on page load - only show when user tries to take action  
  // Show message if redirected from play due to unverified email
  const message = location.state?.message;

  if (loading) return <p className="p-8 text-center">Loading…</p>;
  
  return (
    <>
      {verified && (
        <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-3 text-green-900">
          <p><strong>Email verified!</strong> Your account is now fully activated.</p>
        </div>
      )}
      {message && (
        <div className="mb-4 rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-yellow-900">
          <p><strong>{message}</strong></p>
        </div>
      )}
      <Dashboard 
        acct={account} 
        onLogout={async()=>{ 
          // Capture account info before clearing
          const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
          
          // Set flags FIRST (synchronously) before clearing account state to prevent race condition
          // This ensures that when redirect check fires, useAccount() will see the flag
          if (wasGuest) {

            // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
            const { setLoggedOutFlag } = await import('./lib/guest-storage');
            await setLoggedOutFlag(true);
          } else {
            // Regular user - set sessionStorage flag FIRST (synchronously)
            // Also clear any guest session that might exist (for users who were guests but completed sign-up)
            sessionStorage.setItem('regularUserLoggedOut', 'true');
            const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
            await clearGuestSession();
            await setLoggedOutFlag(false); // Clear any guest logged out flag
            await api("/logout", { method: "POST" });
          }

          // Clear regular user JWT token
          setRegularUserToken(null);

          // Clear account state after flags are set (so redirect check sees the flag)
          setAccount(null);

          // Navigate to home after session is invalidated
          navigate("/");
        }}
        onEnterMine={async () => {
          if (!account) {
            // Show login/register modal
            setAuthMode('login');
            setPendingPlayNavigation(true); // Track that we want to go to /play after guest flow
            setShowAuthModal(true);
          } else {
            // Account exists, navigate to play
            navigate('/play');
          }
        }}
      />

      <OutOfGearModal 
        isOpen={showOutOfGearModal}
        onClose={() => setShowOutOfGearModal(false)}
      />

      <UsernameSelectionModal
        isOpen={showUsernameModal}
        onClose={() => {
          // Allow dismissing - user can try again later
          setShowUsernameModal(false);
          setPendingPlayNavigation(false);
        }}
        onSuccess={async (username) => {
          setShowUsernameModal(false);
          // Wait for account to refresh before navigating
          await refresh();
          // Small delay to ensure account state is updated
          await new Promise(resolve => setTimeout(resolve, 100));
          // Navigate to play after username is set
          if (pendingPlayNavigation) {
            setPendingPlayNavigation(false);
            navigate('/play');
          }
        }}
        apiBase={API_BASE}
      />

      {/* Limit Reached Modal - shown when user has played 7 games (now dismissible) */}
      <LimitReachedModal
        isOpen={showLimitReachedModal}
        onClose={() => {
          setShowLimitReachedModal(false);
        }}
        onCompleteRegistration={async () => {
          setShowLimitReachedModal(false);
          await refresh(); // Refresh account after completing registration
        }}
        apiBase={API_BASE}
        hasEmail={account?.email ? !account.email.endsWith('@guest.local') : false}
        allowClose={true}
        title="Continue Playing"
      />

      {/* Complete Registration Modal - shown when user clicks "Complete Registration" button (dismissible) */}
      <LimitReachedModal
        isOpen={showCompleteRegistrationModal}
        onClose={() => {
          setShowCompleteRegistrationModal(false);
        }}
        onCompleteRegistration={async () => {
          setShowCompleteRegistrationModal(false);
          await refresh(); // Refresh account after completing registration
        }}
        apiBase={API_BASE}
        hasEmail={account?.email ? !account.email.endsWith('@guest.local') : false}
        allowClose={true}
        title="Complete Registration"
      />

      {/* Auth Modal for non-logged-in users */}
      {showAuthModal && (
        <div 
          className="fixed inset-0 z-50 flex justify-center"
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            padding: isMobileLandscape ? '0' : '0.5rem',
            paddingTop: isMobileLandscape 
              ? 'env(safe-area-inset-top, 0px)' 
              : 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            paddingBottom: isMobileLandscape ? '0' : '0.5rem',
            paddingLeft: isMobileLandscape ? '0.25rem' : '0.5rem',
            paddingRight: isMobileLandscape ? '0.25rem' : '0.5rem',
            alignItems: 'flex-start'
          }}
          onClick={() => setShowAuthModal(false)}
        >
          <div 
            className={`border-4 text-center overflow-y-auto relative ${isMobileLandscape ? 'rounded-xl p-1.5 w-full max-w-[420px]' : 'rounded-2xl p-4 md:p-8 w-full max-w-[90vw] md:w-[400px]'}`}
            style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              maxHeight: isMobileLandscape 
                ? (isAndroid ? 'calc(100vh - 32px)' : 'calc(100vh - env(safe-area-inset-top, 0px))')
                : (isAndroid ? 'calc(100vh - 32px - 1rem - 0.5rem)' : 'calc(100vh - env(safe-area-inset-top, 0px) - 1rem - 0.5rem)')
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAuthModal(false)}
              className={`absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-xl font-bold leading-none flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors ${isMobileLandscape ? 'w-8 h-8' : 'text-2xl w-8 h-8'}`}
              style={{ zIndex: 10 }}
            >
              ✕
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.25rem' : '1rem' }}>
              {authMode === 'login' ? (
                <>
                  <Login 
                    onSuccess={async () => {
                      setShowAuthModal(false);
                      // Small delay to ensure cookie is stored by WKWebView
                      await new Promise(resolve => setTimeout(resolve, 100));
                      // Refresh account - this will fetch /account with the session cookie
                      try {
                        await refresh();
                        const isIOS = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
                        if (isIOS) {
                          console.log("[Login] Account refresh completed after login");
                        }
                      } catch (err) {
                        console.error("[Login] Failed to refresh account after login:", err);
                        // If refresh fails, it might be a cookie issue
                        // The account state will remain null, user will see login screen again
                      }
                    }}
                    onGuestClick={async () => {
                      setShowAuthModal(false);
                      // Clear logged out flag so we can log back in
                      const { setLoggedOutFlag } = await import('./lib/guest-storage');
                      await setLoggedOutFlag(false);

                      // Check if guest account already exists with username
                      try {
                        const { getValidGuestToken } = await import('./lib/guest-auth');
                        const token = await getValidGuestToken();
                        if (token) {
                          // Try to fetch account to see if username is already set
                          try {
                            const accountData = await api<Account>("/account");
                            if (accountData && accountData.username && 
                                !accountData.username.startsWith('Guest_') && 
                                !accountData.username.startsWith('guest_')) {
                              // Account exists with username - just refresh and navigate
                              await refresh();
                              if (pendingPlayNavigation) {
                                setPendingPlayNavigation(false);
                                navigate('/play');
                              }
                              return;
                            }
                          } catch (e) {
                            // Account fetch failed, continue to username modal
                          }
                        }
                        // No existing account or no username - show username modal
                        setShowUsernameModal(true);
                      } catch (e) {
                        // Error checking - show username modal as fallback
                        setShowUsernameModal(true);
                      }
                    }}
                    onForgotPasswordClick={isMobile ? () => setAuthMode('forgot') : undefined}
                  />
                  <div className={`text-center ${isMobileLandscape ? 'text-[11px] mt-0.5' : 'text-sm'}`} style={{ color: '#3e2615' }}>
                    New to Goldrush?{' '}
                    <button 
                      className="underline" 
                      onClick={() => setAuthMode('register')}
                      style={{ color: '#3e2615' }}
                    >
                      Create account
                    </button>
                  </div>
                </>
              ) : authMode === 'register' ? (
                <>
                  <Register onSuccess={() => {
                    setShowAuthModal(false);
                    refresh();
                  }} />
                  <div className={`text-center ${isMobileLandscape ? 'text-[11px] mt-0.5' : 'text-sm'}`} style={{ color: '#3e2615' }}>
                    Already have an account?{' '}
                    <button 
                      className="underline" 
                      onClick={() => setAuthMode('login')}
                      style={{ color: '#3e2615' }}
                    >
                      Sign in
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <ForgotPasswordForm 
                    onSuccess={() => {
                      setAuthMode('login');
                    }} 
                    onBack={() => setAuthMode('login')} 
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

}

function StorePage() {
  const { account, loading, refresh, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
    
    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag
    if (wasGuest) {

      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      await api("/logout", { method: "POST" });
    }
    
    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);

    // Navigate to home after session is invalidated
    navigate("/");
  };

  // Allow access even if not logged in or not verified
  // Listen for account updates from other pages
  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  if (loading) return <p className="p-8 text-center">Loading…</p>;
  if (!account) {
    // Not logged in - redirect to home
    navigate("/");
    return null;
  }

  // Allow guests to visit store (they'll be prompted to complete registration when trying to buy)
  return <Store account={account} onAccountRefresh={refresh} onLogout={handleLogout} />;
}


function LeaderboardPage() {
  const { account, loading, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
    
    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag
    if (wasGuest) {
      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      await api("/logout", { method: "POST" });
    }
    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);
    
    // Navigate to home after session is invalidated
    navigate("/");
  };

  // Allow access even if not logged in or not verified
  // Listen for account updates from other pages
  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  // Listen for showLoginModal event (from Store link for guests)
  useEffect(() => {
    const handleShowLoginModal = () => {
      // Navigate to home page where Gate component will handle showing the modal
      navigate("/", { state: { showRegisterModal: true } });
    };
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, [navigate]);

  if (loading) return <p className="p-8 text-center">Loading…</p>;
  if (!account) {
    // Not logged in - redirect to home
    navigate("/");
    return null;
  }

  return <Leaderboard account={account} onLogout={handleLogout} />;
}

function AchievementsPageWrapper() {
  const { account, loading, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');

    if (wasGuest) {
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false);
      await api("/logout", { method: "POST" });
    }

    setAccount(null);
    navigate("/");
  };

  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  useEffect(() => {
    const handleShowLoginModal = () => {
      navigate("/", { state: { showRegisterModal: true } });
    };
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, [navigate]);

  if (loading) return <p className="p-8 text-center">Loading…</p>;
  if (!account) {
    navigate("/");
    return null;
  }

  return <AchievementsPage account={account} onLogout={handleLogout} />;
}
function ContactPage() {
  const { account, loading, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');

    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag    
    if (wasGuest) {
      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {

      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      await api("/logout", { method: "POST" });
    }

    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);
    
    // Navigate to home after session is invalidated
    navigate("/");

  };

  if (loading) return <p className="p-8 text-center">Loading…</p>;

  return <Contact account={account} onLogout={account ? handleLogout : undefined} />;
}

function DeleteAccountPage() {
  const { account, loading, setAccount, refresh } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');

    if (wasGuest) {
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false);
      await api("/logout", { method: "POST" });
    }

    setAccount(null);
    navigate("/");
  };

  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  if (loading) return <p className="p-8 text-center">Loading…</p>;

  return <DeleteAccount account={account} onLogout={account ? handleLogout : undefined} onAccountUpdate={setAccount} />;
}

function PrivacyPolicyPage() {
  const { account, loading, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
    
    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag
    if (wasGuest) {
      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      await api("/logout", { method: "POST" });
    }
    
    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);
    
    // Navigate to home after session is invalidated
    navigate("/");
  };

  if (loading) return <p className="p-8 text-center">Loading…</p>;

  // Privacy policy is accessible to everyone (no login required)
  return <PrivacyPolicy account={account} onLogout={account ? handleLogout : undefined} />;
}

function TournamentsPageWrapper() {
  const { account, loading, refresh, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
    
    if (wasGuest) {
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false);
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      await api("/logout", { method: "POST" });
    }
    
    setAccount(null);
    navigate("/");
  };

  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    const handleAccountNeedsRefresh = async () => {
      await refresh();
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    window.addEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
    return () => {
      window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
      window.removeEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
    };
  }, [setAccount, refresh]);

  if (loading) return <p className="p-8 text-center">Loading…</p>;
  if (!account) {
    navigate("/");
    return null;
  }

  return <TournamentsPage account={account} onAccountRefresh={refresh} onLogout={handleLogout} />;
}

function AccountPageWrapper() {
  const { account, loading, refresh, setAccount } = useAccount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');

    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag    
    if (wasGuest) {
      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      await api("/logout", { method: "POST" });
    }
    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);
    
    // Navigate to home after session is invalidated
    navigate("/");
  };

  // Allow access even if not logged in (they'll see login prompt)
  // Listen for account updates from other pages
  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    return () => window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
  }, [setAccount]);

  // Listen for showLoginModal event (from Store link for guests)
  useEffect(() => {
    const handleShowLoginModal = () => {
      // Navigate to home page where Gate component will handle showing the modal
      navigate("/", { state: { showRegisterModal: true } });
    };
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, [navigate]);

  if (!account) {
    // Not logged in - redirect to home
    navigate("/");
    return null;
  }

  return <AccountPage account={account} onAccountRefresh={refresh} onLogout={handleLogout} />;
}

function Play() {
  const { account, loading, setAccount, refresh } = useAccount();
  const navigate = useNavigate();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const { isMobile, isPortrait } = viewport;
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor;
  const isAndroid = isCapacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = isCapacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  const isIOSPad = isIOSApp && Math.max(viewport.width, viewport.height) >= 768;
  const userAgent = typeof navigator !== 'undefined'
    ? (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase()
    : '';
  const platform = typeof navigator !== 'undefined' ? (((navigator as any).platform || '') as string).toLowerCase() : '';
  const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
  const isIOSDevice = /ipad|iphone|ipod/.test(userAgent);
  const isIOS = isIOSDevice || isIOSApp;
  const isNativeApp = isAndroid || isIOSApp;
  const overlayAlignItems = isNativeApp ? 'center' : 'start';
  const overlayPadding = isNativeApp ? '24px 12px' : (isMobile ? '80px 12px 40px' : '140px 16px 64px');
  // Bulletproof iPhone detection (exclude iPad, including iPadOS that reports as Mac)
  const isIPhone = (() => {
    if (!isIOS) return false;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);

    if (userAgent.includes('ipad')) return false;
    if (platform === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    if (maxDim >= 768 && minDim >= 768) return false;

    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    if (userAgent.includes('ipod')) return true;
    if (isIOSApp && !isIOSPad) return true;
    if (isIOSDevice && !isIOSPad && (maxDim < 768 || minDim < 768)) return true;

    return false;
  })();
  // Use Android phone layout on iPhone (native + mobile Safari), but keep menu untouched
  const usesAndroidPhoneLayout = isAndroid || isIPhone;
  const isAndroidTablet = viewport.isAndroidTablet;
  const androidContentShift = isAndroidTablet && isMobileLandscape ? 8 : 0;
  // Disable legacy portrait layout on web; use main layout for consistency
  const useLegacyMobilePortrait = false;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const levelFrameRef = useRef<HTMLDivElement | null>(null);
  const [levelArtSize, setLevelArtSize] = useState<{ width: number; height: number } | null>(null);
  const [scrollRect, setScrollRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [playRect, setPlayRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [levelCoverTx, setLevelCoverTx] = useState<{ scale: number; offsetX: number; offsetY: number } | null>(null);
  const [rowArtSize, setRowArtSize] = useState<{ width: number; height: number } | null>(null);
  const startMatchRef = useRef<(() => void) | null>(null);
  const [levelFrameMounted, setLevelFrameMounted] = useState(false);
  const playStampButtonRef = useRef<HTMLButtonElement | null>(null);
  const [playPressed, setPlayPressed] = useState(false);
  const playPointerIdRef = useRef<number | null>(null);
  const suppressPlayClickRef = useRef(false);
  const levelScrollRef = useRef<HTMLDivElement | null>(null);
  const [showLevelTopIndicator, setShowLevelTopIndicator] = useState(false);
  const [showLevelBottomIndicator, setShowLevelBottomIndicator] = useState(false);

  useEffect(() => {
    if (isMobile) {
      setIsFullscreen(false);
    }
  }, [isMobile]);

  // Level select art sizing and mask detection
  useEffect(() => {
    let cancelled = false;
    loadImage("LevelSelect.png")
      .then((img) => {
        if (!cancelled) {
          setLevelArtSize({ width: img.naturalWidth, height: img.naturalHeight });
        }
      })
      .catch((err) => {
        console.error("[LevelSelect] Failed to load LevelSelect.png:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if ((import.meta as any).env?.DEV) {
      console.log("[LevelSelectMask] Starting to load LevelSelectMask.png");
    }
    loadImage("LevelSelectMask.png")
      .then((img) => {
        if ((import.meta as any).env?.DEV) {
          console.log("[LevelSelectMask] Image loaded, dimensions:", img.naturalWidth, img.naturalHeight);
        }
        if (cancelled) {
          return;
        }
        setLevelArtSize((prev) => prev || { width: img.naturalWidth, height: img.naturalHeight });
        if ((import.meta as any).env?.DEV) {
          console.log("[LevelSelectMask] Extracting opaque rects...");
        }
        const rects = extractOpaqueRects(img).sort((a, b) => b.width * b.height - a.width * a.height);
        if (rects.length > 0) {
          if ((import.meta as any).env?.DEV) {
            console.log("[LevelSelectMask] Using largest rect for scroll:", rects[0]);
          }
          setScrollRect(rects[0]);
        }
        if (rects.length > 1) {
          if ((import.meta as any).env?.DEV) {
            console.log("[LevelSelectMask] Using second rect for play button:", rects[1]);
          }
          setPlayRect(rects[1]);
        }
        if (rects.length === 0) {
          const rect = extractOpaqueRect(img);
          if ((import.meta as any).env?.DEV) {
            console.log("[LevelSelectMask] Fallback extractOpaqueRect result:", rect);
          }
          if (rect) {
            setScrollRect(rect);
          } else {
            console.error("[LevelSelectMask] extractOpaqueRect returned null - this will cause 'Preparing layout' to show!");
          }
        }
      })
      .catch((err) => {
        console.error("[LevelSelectMask] Failed to load LevelSelectMask.png:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadImage("LevelSelectRow.png")
      .then((img) => {
        if (!cancelled) {
          setRowArtSize({ width: img.naturalWidth, height: img.naturalHeight });
        }
      })
      .catch((err) => {
        console.error("[LevelSelectRow] Failed to load LevelSelectRow.png:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Compute cover transform for level select art (same pipeline as store)
  useEffect(() => {
    if (!levelArtSize || !levelFrameMounted) {
      if ((import.meta as any).env?.DEV && typeof window !== 'undefined' && (window as any).Capacitor) {
        console.log("[levelCoverTx] Waiting for levelArtSize or element mount. levelArtSize:", !!levelArtSize, "mounted:", levelFrameMounted);
      }
      return;
    }
    const el = levelFrameRef.current;
    if (!el) {
      if ((import.meta as any).env?.DEV && typeof window !== 'undefined' && (window as any).Capacitor) {
        console.log("[levelCoverTx] levelFrameRef.current is null despite mounted flag");
      }
      return;
    }

    const update = () => {
      const r = el.getBoundingClientRect();
      const tx = computeCoverTransform(r.width, r.height, levelArtSize.width, levelArtSize.height);
      if ((import.meta as any).env?.DEV && typeof window !== 'undefined' && (window as any).Capacitor) {
        console.log("[levelCoverTx] Computing transform, rect:", r.width, r.height, "art:", levelArtSize.width, levelArtSize.height, "result:", tx);
      }
      setLevelCoverTx(tx);
    };

    const timeoutId = setTimeout(update, 0);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [levelArtSize, isMobileLandscape, levelFrameMounted]);

  const handleLogout = async () => {
    // Reset game state first to prevent game board from persisting and stop all API calls
    resetGame();
    setGameCompleted(false);
    setCurrentWager(null);
    setShowMatchFound(false);
    setMatchInfo(null);
    setPendingGameStart(null);
    
    // Capture account info before clearing
    const wasGuest = account && !account.email_verified && account.email?.endsWith('@guest.local');
    
    // Set flags FIRST (synchronously) before clearing account state to prevent race condition
    // This ensures that when redirect check fires, useAccount() will see the flag
    if (wasGuest) {
      // Guest user - set logged out flag (keeps session data for reuse, but prevents auto-login)
      const { setLoggedOutFlag } = await import('./lib/guest-storage');
      await setLoggedOutFlag(true);
    } else {
      // Regular user - set sessionStorage flag FIRST (synchronously)
      // Also clear any guest session that might exist (for users who were guests but completed sign-up)
      sessionStorage.setItem('regularUserLoggedOut', 'true');
      const { clearGuestSession, setLoggedOutFlag } = await import('./lib/guest-storage');
      await clearGuestSession();
      await setLoggedOutFlag(false); // Clear any guest logged out flag
      await api("/logout", { method: "POST" });
    }

    // Clear account state after flags are set (so redirect check sees the flag)
    setAccount(null);
    
    // Navigate to home after session is invalidated
    navigate("/");
  };

  // Listen for account updates from other pages
  useEffect(() => {
    const handleAccountUpdate = (e: CustomEvent) => {
      setAccount(e.detail);
    };
    const handleAccountNeedsRefresh = async () => {
      await refresh();
    };

    window.addEventListener('accountUpdated', handleAccountUpdate as EventListener);
    window.addEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
    return () => {
      window.removeEventListener('accountUpdated', handleAccountUpdate as EventListener);
      window.removeEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
    };
  }, [setAccount, refresh]);

  // Listen for showLoginModal event (from Store link for guests)
  useEffect(() => {
    const handleShowLoginModal = () => {
      // Navigate to home page where Gate component will handle showing the modal
      navigate("/", { state: { showRegisterModal: true } });
    };
    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, [navigate]);

  // Level selection state
  const [selectedWager, setSelectedWager] = useState<number | null>(null);
  const [availableLevels, setAvailableLevels] = useState<any>(null);
  const [levelsLoading, setLevelsLoading] = useState(true);

  const [gameError, setGameError] = useState<string | null>(null);
  const [gameLoading, setGameLoading] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
    
  // Match found screen state
  const [showMatchFound, setShowMatchFound] = useState(false);
  const [matchInfo, setMatchInfo] = useState<{
    opponentUsername: string;
    wager: number;
  } | null>(null);
  const [pendingGameStart, setPendingGameStart] = useState<(() => Promise<void>) | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Video ad state
  const [showVideoAd, setShowVideoAd] = useState(false);
  const [pendingGameStartAfterAd, setPendingGameStartAfterAd] = useState<(() => Promise<void>) | null>(null);
  const [pendingMatchmakingStart, setPendingMatchmakingStart] = useState<(() => Promise<void>) | null>(null);
  const videoAdCompletedRef = useRef(false); // Guard to prevent multiple completions

  const [currentWager, setCurrentWager] = useState<number | null>(null); // Track wager for game over screen
  const [showOutOfGearModal, setShowOutOfGearModal] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  // Username modal is handled on home page, not here

  // Always call useRoom hook - don't call it conditionally
  const { id, setId, state, play, newGame, newMatchedGame, nextRound, completeGame, resetGame } = useRoom(API);
  const { setSafeToShow, setMatchId, unlockAchievements, refresh: refreshAchievements } = useAchievements();
  const lastRevealKeyRef = useRef<string | null>(null);
  const lastRevealSeenRef = useRef<string | null>(null);
  const lastRevealScoresRef = useRef<{ you: number; opp: number } | null>(null);
  const lastMatchKeyRef = useRef<string | null>(null);
  const prevScoresRef = useRef<{ you: number; opp: number } | null>(null);
  const analyticsGameIdRef = useRef<string | null>(null);
  const lastRoundReportedRef = useRef<number | null>(null);
  const roundStartTimeRef = useRef<number | null>(null);
  const lastRevealEventRef = useRef<string | null>(null);
  const tieCountRef = useRef<number>(0);
  const maxRoundRef = useRef<number>(0);
  const completedGameIdsRef = useRef<Set<string>>(new Set());

  const safeTrack = useCallback((name: string, props?: Record<string, unknown>) => {
    import('./analytics/track')
      .then(({ track }) => track(name, props))
      .catch((err) => console.warn('[analytics] track load failed', err));
  }, []);

  // Handle fullscreen toggle (desktop only)
  const toggleFullscreen = useCallback(() => {
    if (isMobile) return; // Never allow on mobile
    setIsFullscreen(prev => !prev);
  }, [isMobile]);

  const handlePlay = useCallback(async (nugget: number) => {
    const roundNumber = state?.round ?? undefined;
    const startedAt = roundStartTimeRef.current;
    const timeToPick = startedAt ? Math.max(0, Date.now() - startedAt) : undefined;

    safeTrack('nugget_selected', {
      round_number: roundNumber,
      weight: nugget,
      ...(typeof timeToPick === 'number' ? { time_to_pick_ms: timeToPick } : {}),
    });

    // Haptic feedback on nugget selection
    import('./lib/haptics').then(({ hapticLight }) => hapticLight()).catch(() => {});

    try {
      await play(nugget);
    } catch (err) {
      console.error('[Play] Failed to submit move', err);
    }
  }, [play, safeTrack, state?.round]);

  useEffect(() => {
    // Clear error when matchmaking starts, match is found, or game state exists
    if (showMatchFound || state || isSearching) {
      setGameError(null);
    }
  }, [gameError, showMatchFound, state, isSearching]);

  useEffect(() => {
    setMatchId(id || null);
    lastRevealKeyRef.current = null;
    lastRevealSeenRef.current = null;
    lastRevealScoresRef.current = null;
    lastMatchKeyRef.current = null;
    prevScoresRef.current = null;
    analyticsGameIdRef.current = null;
    lastRoundReportedRef.current = null;
    roundStartTimeRef.current = null;
    lastRevealEventRef.current = null;
    tieCountRef.current = 0;
    maxRoundRef.current = 0;
  }, [id, setMatchId]);

  useEffect(() => {
    const safe = !state || state.finished || state.ui_phase === "interlude";
    setSafeToShow(safe);
  }, [setSafeToShow, state?.finished, state?.ui_phase]);

  useEffect(() => {
    return () => {
      setSafeToShow(true);
      setMatchId(null);
    };
  }, [setMatchId, setSafeToShow]);

  useEffect(() => {
    if (!state) {
      prevScoresRef.current = null;
      lastRevealKeyRef.current = null;
      lastRevealSeenRef.current = null;
      lastRevealScoresRef.current = null;
      return;
    }

    const lastReveal = state.lastReveal;
    const lastKey = lastReveal
      ? `${lastReveal.prize}|${lastReveal.you}|${lastReveal.opp}|${lastReveal.outcome}`
      : null;

    if (lastKey && lastKey !== lastRevealSeenRef.current) {
      lastRevealSeenRef.current = lastKey;
      lastRevealScoresRef.current = prevScoresRef.current;
    }

    if (state.ui_phase === "interlude" && lastKey && lastKey !== lastRevealKeyRef.current) {
      const prevScores = lastRevealScoresRef.current;
      const unlocked: string[] = [];
      if (lastReveal?.outcome === "you" && prevScores) {
        const delta = state.scores.you - prevScores.you;
        if (delta >= 15) {
          unlocked.push("great_heist");
        }
      }
      if (unlocked.length > 0) {
        void unlockAchievements(unlocked, { match_id: id, round: state.round });
      }
      lastRevealKeyRef.current = lastKey;
    }

    prevScoresRef.current = { ...state.scores };
  }, [
    id,
    state,
    state?.round,
    state?.scores?.opp,
    state?.scores?.you,
    state?.ui_phase,
    state?.lastReveal?.outcome,
    state?.lastReveal?.opp,
    state?.lastReveal?.prize,
    state?.lastReveal?.you,
    unlockAchievements,
  ]);

  useEffect(() => {
    if (!state?.finished) return;

    const matchKey = `${id || "match"}|${state.scores.you}|${state.scores.opp}|${state.winner || ""}|${state.endReason || ""}`;
    if (lastMatchKeyRef.current === matchKey) return;
    lastMatchKeyRef.current = matchKey;

    const unlocked: string[] = [];
    if (state.scores.you >= 30) {
      unlocked.push("strike_it_rich");
    }
    if (state.winner === "you" && state.scores.you - state.scores.opp === 1) {
      unlocked.push("narrow_escape");
    }
    if (unlocked.length > 0) {
      void unlockAchievements(unlocked, { match_id: id, round: state.round });
    }
  }, [id, state?.endReason, state?.finished, state?.scores?.opp, state?.scores?.you, state?.round, state?.winner, unlockAchievements]);

  // Exit fullscreen when game ends (optional)
  useEffect(() => {
    if (state?.finished) {
      setIsFullscreen(false);
    }
  }, [state?.finished]);

  const opponentDisplayName = useMemo(() => {
    if (!state) return 'Lump';
    if (state.opponentUsername) return state.opponentUsername;
    if (state.player1Username && state.player2Username) {
      if (state.yourUsername === state.player1Username) {
        return state.player2Username;
      }
      if (state.yourUsername === state.player2Username) {
        return state.player1Username;
      }
    }
    const result = state.player2Username || state.player1Username || state.opponentName || 'Lump';
    console.log('[App opponentDisplayName]', {
      result,
      yourUsername: state.yourUsername,
      opponentUsername: state.opponentUsername,
      player1Username: state.player1Username,
      player2Username: state.player2Username,
      opponentName: state.opponentName,
    });
    return result;
  }, [state]);

  const playerDisplayName = useMemo(() => {
    const result = state?.yourUsername || account?.username || 'You';
    if ((import.meta as any).env?.DEV) {
      console.log('[App playerDisplayName]', {
        result,
        yourUsername: state?.yourUsername,
        accountUsername: account?.username,
      });
    }
    return result;
  }, [state?.yourUsername, account?.username]);

  const mapEndReasonToAbandonReason = useCallback((reason?: string | null) => {
    if (!reason) return undefined;
    if (reason.startsWith('timeout')) return 'timeout';
    if (reason.includes('disconnect')) return 'disconnect';
    if (reason.includes('background')) return 'app_backgrounded';
    if (reason.includes('tournament') || reason.includes('forfeit')) return 'back';
    return 'unknown';
  }, []);

  useEffect(() => {
    if (!state || !id) return;

    if (analyticsGameIdRef.current !== id) {
      analyticsGameIdRef.current = id;
      tieCountRef.current = 0;
      maxRoundRef.current = state.round || 0;
      lastRoundReportedRef.current = null;
      lastRevealEventRef.current = null;
      roundStartTimeRef.current = Date.now();
      safeTrack('game_start', { mode: state.matchType || 'pve', opponent: opponentDisplayName });
    }

    if (state.round && state.round !== lastRoundReportedRef.current) {
      lastRoundReportedRef.current = state.round;
      maxRoundRef.current = Math.max(maxRoundRef.current, state.round);
      roundStartTimeRef.current = Date.now();
      safeTrack('round_start', { round_number: state.round });
    }
  }, [id, opponentDisplayName, safeTrack, state?.matchType, state?.round]);

  useEffect(() => {
    if (!state || !id || !state.lastReveal) return;

    const reveal = state.lastReveal;
    const key = `${id}|${state.round}|${reveal.prize}|${reveal.you}|${reveal.opp}|${reveal.outcome}`;
    if (lastRevealEventRef.current === key) return;
    lastRevealEventRef.current = key;

    const result = reveal.outcome === 'you' ? 'win' : reveal.outcome === 'opp' ? 'loss' : 'tie';
    if (reveal.outcome === 'tie') {
      tieCountRef.current += 1;
    }
    safeTrack('reveal', { round_number: state.round, result });
  }, [id, safeTrack, state?.lastReveal, state?.round]);

  useEffect(() => {
    if (!state || !id || !state.finished) return;
    if (completedGameIdsRef.current.has(id)) return;
    completedGameIdsRef.current.add(id);

    const winner = state.winner === 'you' ? 'player' : state.winner === 'opp' ? 'opponent' : 'tie';
    const roundsPlayed = Math.max(maxRoundRef.current, state.round || 0);
    const outcome = state.winner === 'you' ? 'win' : state.winner === 'opp' ? 'loss' : 'tie';
    safeTrack('game_complete', {
      winner,
      outcome,
      rounds_played: roundsPlayed,
      tie_count: tieCountRef.current,
      round_number: state.round,
    });

    if (state.endReason && state.endReason !== 'rounds_complete') {
      const reason = mapEndReasonToAbandonReason(state.endReason);
      safeTrack('game_abandoned', {
        round_number: state.round,
        ...(reason ? { reason } : {}),
      });
    }
  }, [id, mapEndReasonToAbandonReason, safeTrack, state?.endReason, state?.finished, state?.round, state?.winner]);

  useEffect(() => {
    if (!state?.finished && gameCompleted) {
      setGameCompleted(false);
    }
  }, [state?.finished, gameCompleted]);

  const rowMaskPath = useMemo(() => getAssetPath("LevelSelectRow.png"), []);
  const playOffsetX = useMemo(() => {
    if (isMobile) {
      return isMobileLandscape ? 12 : 0;
    }
    return 10;
  }, [isMobile, isMobileLandscape]);
  const playGrow = useMemo(() => {
    if (isMobile) {
      return isMobileLandscape ? { left: 0, right: 12 } : { left: 0, right: 8 };
    }
    return { left: 1, right: 10 };
  }, [isMobile, isMobileLandscape]);

  // Reset game when returning from game over screen
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('from') === 'gameover') {
      resetGame();
      setCurrentWager(null);
      setGameCompleted(false);
      // Clear any tournament flags (for regular games)
      sessionStorage.removeItem('current_tournament');
      sessionStorage.removeItem('tournament_match_starting');
      sessionStorage.removeItem('tournament_result');
      // Clear the query param after resetting
      navigate('/play', { replace: true });
    }
    // Handle tournament match from tournaments page
    const tournamentId = searchParams.get('tournament');
    const matchId = searchParams.get('match');
    const opponentName = searchParams.get('opponent');
    const existingGid = searchParams.get('gid'); // Game already created by backend
    
    if (tournamentId && matchId && account) {
      // If gid is provided, load the existing game directly (auto-advance flow)
      // Check if we need to load a different game (id is different or not set)
      if (existingGid && (!id || id !== existingGid)) {
        console.log(`[Play] Loading existing tournament game ${existingGid} for match ${matchId} (current id: ${id})`);
        
        // Reset game state if switching to a different game (including tiebreakers)
        if (id && id !== existingGid) {
          console.log(`[Play] Resetting game state from ${id} to ${existingGid} (may be tiebreaker)`);
          resetGame();
        } else if (!id && existingGid) {
          // Loading a new game (could be tiebreaker) - reset to ensure clean state
          console.log(`[Play] Loading new game ${existingGid}, resetting game state`);
          resetGame();
        }
        // Store tournament info for game over screen
        sessionStorage.setItem('current_tournament', JSON.stringify({
          tournamentId: parseInt(tournamentId),
          matchId: parseInt(matchId),
        }));

        // Set tournament flag to bypass level select
        sessionStorage.setItem('tournament_match_starting', 'true');
        
        // Load the existing game by calling newMatchedGame with the gid
        (async () => {
          try {
            await newMatchedGame({ 
              matchType: 'pvp', 
              wager: 0.0,
              gid: existingGid,
              tournamentMatchId: parseInt(matchId)
            });
            // Clear URL params after loading
            navigate('/play', { replace: true });
          } catch (err) {
            console.error('[Play] Failed to load existing tournament game:', err);
            // Fall through to normal flow if loading fails
            navigate('/play', { replace: true });
          }
        })();
        return;
      }
      
      // Otherwise, get tournament match details to create a new game
      (async () => {
        try {
          const headers: HeadersInit = { "Content-Type": "application/json" };
          // Check for regular user JWT token first (all platforms - JWT is primary)
          const regularUserToken = getRegularUserToken();
          if (regularUserToken) {
            headers["Authorization"] = `Bearer ${regularUserToken}`;
          } else {
            // Fallback to guest token
            try {
              const { getValidGuestToken } = await import('./lib/guest-auth');
              const guestToken = await getValidGuestToken();
              if (guestToken) {
                headers["Authorization"] = `Bearer ${guestToken}`;
              }
            } catch {
              // Continue without token
            }
          }
          
          const matchResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}/matches/${matchId}`, {
            credentials: "include",
            headers,
          });
          
          if (!matchResponse.ok) {
            throw new Error("Failed to get match details");
          }
          
          const matchData = await matchResponse.json();
          
          // Get tournament to find wager
          const tournamentResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}`, {
            credentials: "include",
            headers,
          });
          
          if (!tournamentResponse.ok) {
            throw new Error("Failed to get tournament details");
          }
          
          const tournamentData = await tournamentResponse.json();

          // Check if tournament is completed - if so, navigate to tournaments page
          if (tournamentData.status === "completed") {
            console.log('[Play] Tournament is completed, cannot create game');
            sessionStorage.removeItem('current_tournament');
            sessionStorage.removeItem('tournament_match_starting');
            navigate('/tournaments');
            return;
          }
          
          const tournamentInfo = {
            tournamentId: parseInt(tournamentId),
            matchId: parseInt(matchId),
            tournamentName: tournamentData.name,
          };

          // If backend already created a game (latest_gid) join it immediately
          if (!existingGid && !id && !matchData.winner_entry_id && matchData.latest_gid) {
            const inferredGid = matchData.latest_gid as string;
            console.log(`[Play] Found existing tournament game ${inferredGid} for match ${matchId}`);
            sessionStorage.setItem('current_tournament', JSON.stringify(tournamentInfo));
            sessionStorage.setItem('tournament_match_starting', 'true');
            (async () => {
              try {
                await newMatchedGame({
                  matchType: 'pvp',
                  wager: 0.0,
                  gid: inferredGid,
                  tournamentMatchId: parseInt(matchId),
                });
                navigate('/play', { replace: true });
              } catch (err) {
                console.error('[Play] Failed to load existing tournament game via latest_gid:', err);
                const errorMessage = err instanceof Error ? err.message : String(err);
                
                // If tournament is completed, navigate to tournaments page
                if (errorMessage.includes('TOURNAMENT_ALREADY_COMPLETED') || errorMessage.includes('completed') || errorMessage.includes('409')) {
                  console.log('[Play] Tournament is completed, navigating to tournaments page');
                  sessionStorage.removeItem('current_tournament');
                  sessionStorage.removeItem('tournament_match_starting');
                  navigate('/tournaments');
                } else {
                  navigate('/play', { replace: true });
                }
              }
            })();
            return;
          }

          // Tournament matches don't require glint - entry fee (gear) already paid
          const wager = 0.0; // No glint wager for tournament matches
          
          // Get opponent user ID from match data
          // The match endpoint returns entry1 and entry2 with user_id
          // Note: entry2 might be None for a "bye" (automatic win)
          let opponentId: number | undefined;
          if (matchData.entry1?.user_id === account.id) {
            opponentId = matchData.entry2?.user_id;
          } else if (matchData.entry2?.user_id === account.id) {
            opponentId = matchData.entry1?.user_id;
          }
          
          // Fallback to entry1_user_id/entry2_user_id if entry structure not available
          if (!opponentId) {
            if (matchData.entry1_user_id === account.id) {
              opponentId = matchData.entry2_user_id;
            } else if (matchData.entry2_user_id === account.id) {
              opponentId = matchData.entry1_user_id;
            }
          }

          // Safety check: ensure opponentId is not the same as current user
          if (opponentId === account.id) {
            console.error('[Play] Error: opponentId matches current user ID', { opponentId, accountId: account.id, matchData });
            throw new Error("Invalid match: cannot play against yourself");
          }
          // If no opponent (bye), we can still proceed - the backend will handle it
          // Don't throw error, just log it
          
          if (!opponentId) {
            console.log('[Play] Tournament match has no opponent (bye) - match will be auto-won', { matchData });
            // Set a placeholder - backend will handle the bye
            opponentId = undefined;
          }

          // Use opponent_username from match data (more reliable than URL param)
          const actualOpponentName = matchData.opponent_username || opponentName || 'Opponent';

          // Store tournament info for game over screen

          // Store grace period end time for countdown display
          if (matchData.grace_period_ends_at) {
            sessionStorage.setItem('tournament_grace_period_ends_at', matchData.grace_period_ends_at);
          }
          
          // Store game creation to run after the ready-set-go overlay completes
          const gameStartFn = async () => {
            // Only create game if there's an opponent (not a bye)
            if (opponentId) {
              await newMatchedGame({ 
                matchType: 'pvp', 
                wager: wager, 
                opponentId: opponentId,
                opponentUsername: actualOpponentName,
                tournamentMatchId: parseInt(matchId)
              });
              // Store tournament info in sessionStorage so we can access it after game completion
              sessionStorage.setItem('current_tournament', JSON.stringify(tournamentInfo));
            } else {
              // This is a bye - the backend should handle it automatically
              // Just navigate back to tournaments page - the match will be auto-completed
              console.log('[Play] Tournament match is a bye - match will be auto-completed by backend');
              navigate('/tournaments');
            }
          };
          
          // Only show match found if there's an opponent (not a bye)
          if (opponentId) {
            setPendingGameStart(() => gameStartFn);
            setMatchInfo({
              opponentUsername: actualOpponentName,
              wager: wager,
            });
            setShowMatchFound(true);
            // Store tournament flag in sessionStorage to bypass level select (works in incognito)
            sessionStorage.setItem('tournament_match_starting', 'true');
            // Clear query params
            navigate('/play', { replace: true });
          } else {
            // This is a bye - don't show match found, just navigate
            gameStartFn();
          }
        } catch (err) {
          console.error('[Play] Error starting tournament match:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to start tournament match';
          
          // If tournament is completed, navigate to tournaments page instead of showing error
          if (errorMessage.includes('TOURNAMENT_ALREADY_COMPLETED') || errorMessage.includes('completed')) {
            console.log('[Play] Tournament is completed, navigating to tournaments page');
            sessionStorage.removeItem('current_tournament');
            sessionStorage.removeItem('tournament_match_starting');
            navigate('/tournaments');
          } else {
            setGameError(errorMessage);
            navigate('/play', { replace: true });
          }
        }
      })();
    }
  }, [searchParams, navigate, account]); // Removed resetGame from dependencies

  // Check if current match is a tournament match
  const isTournamentMatch = useCallback(() => {
    // Check sessionStorage for tournament flag
    const tournamentFlag = sessionStorage.getItem('tournament_match_starting');
    if (tournamentFlag === 'true') {
      return true;
    }
    
    // Check URL params for tournament
    const urlParams = new URLSearchParams(window.location.search);
    const tournamentId = urlParams.get('tournament');
    if (tournamentId) {
      return true;
    }
    
    return false;
  }, []);

  // Callback for match found screen completion
  const handleMatchScreenComplete = useCallback(async () => {

    // Don't close match found screen yet - keep overlay visible until gameboard loads
    // The overlay will be hidden when state exists (gameboard is ready)
    
    // Check if this is a tournament match - skip ads for tournaments
    if (isTournamentMatch()) {
      console.log('[VideoAd] Skipping ad for tournament match');
      // Start game directly for tournaments
      try {
        if (pendingGameStart) {
          await pendingGameStart();
          setPendingGameStart(null);
        }
        // Don't clear matchInfo yet - let it clear when state is ready
      } catch (err: any) {
        handleGameStartError(err);
        // On error, hide overlay immediately
        setShowMatchFound(false);
        setMatchInfo(null);
      }
      return;
    }
    
    // For regular games, start game directly (ad was already shown before matchmaking)
    try {
      if (pendingGameStart) {
        await pendingGameStart();
        setPendingGameStart(null);
      }
      // Don't clear matchInfo yet - let it clear when state is ready
    } catch (err: any) {
      handleGameStartError(err);
      // On error, hide overlay immediately
      setShowMatchFound(false);
      setMatchInfo(null);
    }
  }, [pendingGameStart, isTournamentMatch]);

  // Hide match found overlay once the game state is ready (failsafe against resize quirks)
  useEffect(() => {
    if (state && showMatchFound) {
      setShowMatchFound(false);
      setMatchInfo(null);
    }
  }, [state, showMatchFound]);

  // Handle game start error
  const handleGameStartError = useCallback((err: any) => {
    // Don't show error if we're already in matchmaking or have a match found
    if (showMatchFound || isSearching) {
      return;
    }

    const status = err?.status ?? err?.response?.status;
    const message = err?.message || 'Failed to start match';
    
    // Only check for explicit error codes or status 409
    // Status 409 is the HTTP Conflict status that the server returns for legitimate "already in game" cases
    // Also check for explicit error message strings that the server sends
    const alreadyActive =
      status === 409 ||
      message.includes('ALREADY_IN_GAME') ||
      message.includes('ALREADY_SEARCHING');

    if (alreadyActive) {
      console.warn('[Match] User already active; not falling back to AI.', err);
      setGameError('You are already in a game or matchmaking. Finish that match before starting a new one.');
      // Clear the matchmaking lock if it exists (might be stale)
      try {
        localStorage.removeItem('matchmaking_lock_v1');
      } catch {}
    } else {
      setGameError(message);
    }

    // Close screens and clear pending start
    setShowMatchFound(false);
    setShowVideoAd(false);
    setPendingGameStart(null);
    setPendingGameStartAfterAd(null);
    setPendingMatchmakingStart(null);
    setMatchInfo(null);
  }, [showMatchFound, isSearching]);

  // Callback for video ad completion
  const handleVideoAdComplete = useCallback(async () => {
    // Guard against multiple calls - the ad completion can be triggered multiple times
    if (videoAdCompletedRef.current) {
      console.log('[VideoAd] Ad completion already handled, ignoring duplicate call');
      return;
    }
    
    console.log('[VideoAd] Ad completed');
    videoAdCompletedRef.current = true;
    
    // Clear the video ad flag so polling can update state
    if (typeof window !== 'undefined') {
      (window as any).__VIDEO_AD_SHOWING__ = false;
    }
    
    // Close the video ad modal
    setShowVideoAd(false);
    
    try {
      // Start matchmaking or game after ad completes
      if (pendingMatchmakingStart) {
        console.log('[VideoAd] Starting matchmaking after ad');
        await pendingMatchmakingStart();
        setPendingMatchmakingStart(null);
      } else if (pendingGameStartAfterAd) {
        console.log('[VideoAd] Starting game after ad');
        await pendingGameStartAfterAd();
        setPendingGameStartAfterAd(null);
      }
      
    } catch (err: any) {
      handleGameStartError(err);
    }

  }, [pendingMatchmakingStart, pendingGameStartAfterAd, handleGameStartError]);

  // Callback for video ad error (proceed to game anyway)
  const handleVideoAdError = useCallback(() => {
    console.log('[VideoAd] Ad error, proceeding to matchmaking');
    // Clear the video ad flag
    if (typeof window !== 'undefined') {
      (window as any).__VIDEO_AD_SHOWING__ = false;
    }
    // Close the video ad modal
    setShowVideoAd(false);
    // Reset the completion guard
    videoAdCompletedRef.current = false;
    // Proceed with matchmaking even though ad failed
    handleVideoAdComplete();
  }, [handleVideoAdComplete]);

  // Don't redirect if no account - allow username modal to show
  // The username modal will handle account creation

  // Fetch available levels (guests don't need email verification)
  // Only fetch if account exists - username modal will create account first
  const fetchLevels = useCallback(async () => {
    if (account) {
      try {
        // Use api() function which includes JWT token for guests
        const data = await api<{balance: string; levels: any[]; gear_charges: number; purchased_gear_charges: number; tournament_gear_charges?: number}>("/levels/available");
        setAvailableLevels(data);
      } catch (err) {
        console.error("Failed to fetch levels:", err);
      } finally {
        setLevelsLoading(false);
      }
    } else {
      // No account yet - stop loading so username modal can show
      setLevelsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchLevels();
  }, [fetchLevels]);

  // Refresh levels when account needs refresh (gear was consumed)
  useEffect(() => {
    const handleAccountNeedsRefresh = () => {
      fetchLevels();
    };
    window.addEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
    return () => window.removeEventListener('accountNeedsRefresh', handleAccountNeedsRefresh as EventListener);
  }, [fetchLevels]);

  // Refresh account when tab becomes visible (handles email verification case)
  // When user verifies email in another tab and returns, this refreshes the account state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - refresh account to check for verification status
        void refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  // Handle game completion (only once per game)
  useEffect(() => {
    // Debug logging for tournament completion
    if (state?.finished && !gameCompleted) {
      // DEV-only: Full debug info
      if ((import.meta as any).env?.DEV) {
        console.log('[Game Complete] Game finished detected:', {
          finished: state.finished,
          winner: state.winner,
          endReason: state.endReason,
          opponentUsername: state.opponentUsername,
          scores: state.scores
        });
      }
      // PROD: Removed - if needed, send to analytics/Sentry as breadcrumb instead
    }
    if (state?.finished && completeGame && !gameCompleted) {
      // If currentWager is not set, try to get it from state or use a default
      const wager = currentWager || 0.01;
      const handleGameComplete = async () => {
        try {
          setGameCompleted(true); // Prevent multiple calls
          let outcome: 'you' | 'opp' | 'tie' = 'tie';
          // First check for timeout - if someone timed out, they lose regardless of score
          if (state.endReason?.startsWith('timeout')) {
            const [, timeoutDetail] = state.endReason.split(':');
            if (timeoutDetail === 'tie') {
              outcome = 'tie';
            } else if (timeoutDetail === 'you') {
              outcome = 'opp';
            } else if (timeoutDetail === 'opp') {
              outcome = 'you';
            }
          }
          // Otherwise, use the winner field from backend (which forfeit sets correctly)
          else if (state.winner) {
            outcome = state.winner;
          }
          // Fallback: compare scores (only when game completes normally)
          else {
            if (state.scores.you > state.scores.opp) {
              outcome = 'you';
          } else if (state.scores.you < state.scores.opp) {
            outcome = 'opp';
          }

          }
          
          const result = await completeGame({
            outcome,
            yourScore: state.scores.you,
            opponentScore: state.scores.opp,
            opponentUsername: opponentDisplayName,
            endReason: state.endReason ?? null,
          });
          recordGameCompleted();

          // Store tournament result if present (for next match info)
          if (result?.tournament_match) {
            try {
              sessionStorage.setItem('tournament_result', JSON.stringify(result.tournament_match));
            } catch {}
          }

          if (result) {
            if (!result.tournament_match) {
              void incrementPlayGamesAchievement("seasoned_prospector", 1);
              void incrementPlayGamesAchievement("veteran_of_the_mine", 1);
            }
            
            // Fetch updated account balance from database after game completion
            // Wait a bit to ensure database commit is complete
            await new Promise(resolve => setTimeout(resolve, 200));
            let updatedBalance = parseFloat(account?.glint_balance || '0');
            try {
              // Use api() function which includes JWT token for guests
              const accountData = await api<Account>("/account");
              updatedBalance = parseFloat(accountData.glint_balance || '0');
              // Update local account state and dispatch event
              await refresh();
              window.dispatchEvent(new CustomEvent('accountUpdated', { detail: accountData }));
            } catch (err) {
              console.error('Failed to fetch updated account:', err);
              // Fall back to refreshing account hook
              await refresh();
            }
            void refreshAchievements();

            // Delay navigation to let timeout/tie animations and audio finish
            // Keep this tight so the game over screen doesn't feel laggy
            const timeoutHoldMs = state.endReason?.startsWith('timeout') ? 600 : 250;

            // For tied games, wait for broken scale animation (1000ms)
            const tieHoldMs = outcome === 'tie' ? 1000 : 0;
            const delay = Math.max(timeoutHoldMs, tieHoldMs);
            
            setTimeout(async () => {
              // Check if this was a tournament match
              let tournamentInfo: any = null;
              let nextMatchInfo: any = null;
              let tournamentData: any = null; // Declare in outer scope so it's accessible later
              try {
                const stored = sessionStorage.getItem('current_tournament');
                if (stored) {
                  tournamentInfo = JSON.parse(stored);
                  const tournamentId = tournamentInfo.tournamentId;
                  
                  // Fetch tournament details for entry fee and top prize
                  try {
                    const headers: HeadersInit = { "Content-Type": "application/json" };
                    // Check for regular user JWT token first (all platforms - JWT is primary)
                    const regularUserToken = getRegularUserToken();
                    if (regularUserToken) {
                      headers["Authorization"] = `Bearer ${regularUserToken}`;
                    } else {
                      // Fallback to guest token
                      try {
                        const { getValidGuestToken } = await import('./lib/guest-auth');
                        const guestToken = await getValidGuestToken();
                        if (guestToken) {
                          headers["Authorization"] = `Bearer ${guestToken}`;
                        }
                      } catch {
                        // Continue without token
                      }
                    }
                    
                    // Fetch tournament details
                    const tournamentResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}`, {
                      credentials: "include",
                      headers,
                    });
                    
                    if (tournamentResponse.ok) {
                      tournamentData = await tournamentResponse.json();
                      if (tournamentData) {
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.entryFeeGear = tournamentData.gear_cost || undefined;
                        
                        // Get top prize (1st place) - prioritize current_prize_table (calculated) over config.prize_table (hardcoded)
                        // This uses the same data source as the tournament tile
                        const currentPrizeTable = tournamentData.current_prize_table;
                        const configPrizeTable = tournamentData.config?.prize_table || {};
                        const topPrize = currentPrizeTable?.["1"] || configPrizeTable["1"] || {};

                        // Always set top prize values (even if 0) to ensure consistency with tournament tile
                        tournamentInfo.topPrizeGlint = topPrize.glint !== undefined ? topPrize.glint : undefined;
                        tournamentInfo.topPrizeGear = topPrize.gear !== undefined ? topPrize.gear : undefined;
                        
                        console.log('[Game Complete] Set top prize from tournament data:', {
                          topPrizeGlint: tournamentInfo.topPrizeGlint,
                          topPrizeGear: tournamentInfo.topPrizeGear,
                          currentPrizeTable,
                          configPrizeTable
                        });
                      }
                    }
                  } catch (err) {
                    console.error('[Game Complete] Failed to fetch tournament details:', err);
                  }

                  // Fetch current match from endpoint (more reliable than sessionStorage)
                  try {
                    const headers: HeadersInit = { "Content-Type": "application/json" };
                    // Check for regular user JWT token first (all platforms - JWT is primary)
                    const regularUserToken = getRegularUserToken();
                    if (regularUserToken) {
                      headers["Authorization"] = `Bearer ${regularUserToken}`;
                    } else {
                      // Fallback to guest token
                      try {
                        const { getValidGuestToken } = await import('./lib/guest-auth');
                        const guestToken = await getValidGuestToken();
                        if (guestToken) {
                          headers["Authorization"] = `Bearer ${guestToken}`;
                        }
                      } catch {
                        // Continue without token
                      }
                    }
                    
                    const matchResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}/my-match`, {
                      credentials: "include",
                      headers,
                    });
                    
                    if (matchResponse.ok) {
                      const matchData = await matchResponse.json();
                      if (matchData?.match) {
                        nextMatchInfo = {
                          id: matchData.match.id,
                          tournament_id: tournamentId,
                          gid: matchData.match.gid || undefined, // Use gid from endpoint if available
                        };
                        console.log('[Game Complete] Fetched next match from endpoint:', nextMatchInfo);
                      } else {
                        // No pending match from endpoint - check tournament_result from sessionStorage
                        // This handles cases where the match was just created but endpoint hasn't updated yet
                        const tournamentResultStr = sessionStorage.getItem('tournament_result');
                        if (tournamentResultStr) {
                          const tournamentResult = JSON.parse(tournamentResultStr);
                          // TIEBREAKER LOGIC: Check if this is a tournament match that needs a tiebreaker
                          if (tournamentResult?.tiebreaker_game_id) {
                            const matchId = tournamentResult.tiebreaker_match_id || tournamentInfo?.matchId;
                            const tournamentId = tournamentResult.tiebreaker_tournament_id || tournamentInfo?.tournamentId;
                            if (matchId && tournamentId) {
                              nextMatchInfo = {
                                id: matchId,
                                tournament_id: tournamentId,
                                gid: tournamentResult.tiebreaker_game_id,
                                is_tiebreaker: true,
                              };
                              console.log('[Game Complete] Using tiebreaker info from tournament_result:', nextMatchInfo);
                              // Don't return here - continue to show game over screen with tiebreaker info
                            }
                          }
                          // Check if tournament is completed
                          else if (tournamentResult?.advancement?.completed || tournamentResult?.tournament_completed) {
                            // Tournament is completed - winner should see GameOverScreenV2
                            console.log('[Game Complete] Tournament completed, winner should see GameOverScreenV2');
                            tournamentInfo = tournamentInfo || {};
                            tournamentInfo.tournamentCompleted = true;
                            tournamentInfo.userEliminated = false; // Winner is not eliminated
                            nextMatchInfo = null; // No next match

                          } else if (tournamentResult?.next_match) {
                            // Check for next match first (most important)
                            nextMatchInfo = tournamentResult.next_match;
                            if (tournamentResult?.next_match_gid) {
                              nextMatchInfo.gid = tournamentResult.next_match_gid;
                            }
                            console.log('[Game Complete] Using next_match from tournament_result:', nextMatchInfo);
                          }
                          // Check for elimination status
                          if (tournamentResult?.user_eliminated) {
                            tournamentInfo = tournamentInfo || {};
                            tournamentInfo.userEliminated = true;
                          }
                          // Get placement from tournament_result
                          if (tournamentResult?.user_final_placement !== undefined) {
                            tournamentInfo = tournamentInfo || {};
                            tournamentInfo.userFinalPlacement = tournamentResult.user_final_placement;
                          }
                          // ALWAYS use prize info from tournament_result (backend helper already calculated it correctly)
                          // The backend helper uses the same calculate_gear_prizes function as the tournament tiles
                          if (tournamentResult?.user_prize_glint !== undefined && tournamentResult.user_prize_glint !== null) {
                            tournamentInfo = tournamentInfo || {};
                            tournamentInfo.userPrizeGlint = tournamentResult.user_prize_glint;
                            console.log('[Game Complete] Set userPrizeGlint from tournament_result (backend helper):', tournamentResult.user_prize_glint);
                          }
                          if (tournamentResult?.user_prize_gear !== undefined && tournamentResult.user_prize_gear !== null) {
                            tournamentInfo = tournamentInfo || {};
                            tournamentInfo.userPrizeGear = tournamentResult.user_prize_gear;
                            console.log('[Game Complete] Set userPrizeGear from tournament_result (backend helper):', tournamentResult.user_prize_gear);
                          }
                          
                          // Only fallback to current_prize_table if backend didn't provide prize info
                          if ((tournamentInfo.userPrizeGlint === undefined || tournamentInfo.userPrizeGlint === null) && 
                              (tournamentInfo.userPrizeGear === undefined || tournamentInfo.userPrizeGear === null) &&
                              tournamentData && tournamentInfo.userFinalPlacement) {
                            const placementStr = String(tournamentInfo.userFinalPlacement);
                            const currentPrizeTable = tournamentData.current_prize_table;
                            const configPrizeTable = tournamentData.config?.prize_table || {};
                            
                            // Get user's prize from current_prize_table (same as tournament tile)
                            const userPrize = currentPrizeTable?.[placementStr] || configPrizeTable[placementStr] || {};
                            if (userPrize.glint !== undefined) {
                              tournamentInfo.userPrizeGlint = userPrize.glint;
                            }
                            if (userPrize.gear !== undefined) {
                              tournamentInfo.userPrizeGear = userPrize.gear;
                            }
                            
                            console.log('[Game Complete] Fallback: Set prize info from current_prize_table:', {
                              placement: placementStr,
                              userPrizeGlint: tournamentInfo.userPrizeGlint,
                              userPrizeGear: tournamentInfo.userPrizeGear
                            });
                          }
                          sessionStorage.removeItem('tournament_result');
                        }
                      }
                    }
                  } catch (err) {
                    console.error('[Game Complete] Failed to fetch next match:', err);
                    // Fallback to sessionStorage if endpoint fails
                    const tournamentResultStr = sessionStorage.getItem('tournament_result');
                    if (tournamentResultStr) {
                      const tournamentResult = JSON.parse(tournamentResultStr);
                      // TIEBREAKER LOGIC: Check if this is a tournament match that needs a tiebreaker
                      if (tournamentResult?.tiebreaker_game_id) {
                        const matchId = tournamentResult.tiebreaker_match_id || tournamentInfo?.matchId;
                        const tournamentId = tournamentResult.tiebreaker_tournament_id || tournamentInfo?.tournamentId;
                        if (matchId && tournamentId) {
                          nextMatchInfo = {
                            id: matchId,
                            tournament_id: tournamentId,
                            gid: tournamentResult.tiebreaker_game_id,
                            is_tiebreaker: true,
                          };
                          console.log('[Game Complete] Using tiebreaker info from tournament_result (fallback):', nextMatchInfo);
                        }
                      }
                      // Check if tournament is completed
                      else if (tournamentResult?.advancement?.completed || tournamentResult?.tournament_completed) {
                        // Tournament is completed - winner should see GameOverScreenV2
                        console.log('[Game Complete] Tournament completed (fallback), winner should see GameOverScreenV2');
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.tournamentCompleted = true;
                        tournamentInfo.userEliminated = false; // Winner is not eliminated
                        nextMatchInfo = null; // No next match
                      } else if (tournamentResult?.next_match) {
                        nextMatchInfo = tournamentResult.next_match;
                        if (tournamentResult?.next_match_gid) {
                          nextMatchInfo.gid = tournamentResult.next_match_gid;
                        }
                      }
                      if (tournamentResult?.user_eliminated) {
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.userEliminated = true;
                      }
                      // Get placement from tournament_result
                      if (tournamentResult?.user_final_placement !== undefined) {
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.userFinalPlacement = tournamentResult.user_final_placement;
                      }

                      // ALWAYS use prize info from tournament_result (backend helper already calculated it correctly)
                      // The backend helper uses the same calculate_gear_prizes function as the tournament tiles
                      if (tournamentResult?.user_prize_glint !== undefined && tournamentResult.user_prize_glint !== null) {
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.userPrizeGlint = tournamentResult.user_prize_glint;
                        console.log('[Game Complete] Set userPrizeGlint from tournament_result (fallback, backend helper):', tournamentResult.user_prize_glint);
                      }
                      if (tournamentResult?.user_prize_gear !== undefined && tournamentResult.user_prize_gear !== null) {
                        tournamentInfo = tournamentInfo || {};
                        tournamentInfo.userPrizeGear = tournamentResult.user_prize_gear;
                        console.log('[Game Complete] Set userPrizeGear from tournament_result (fallback, backend helper):', tournamentResult.user_prize_gear);
                      }
                      
                      // Only fallback to current_prize_table if backend didn't provide prize info
                      if ((tournamentInfo.userPrizeGlint === undefined || tournamentInfo.userPrizeGlint === null) && 
                          (tournamentInfo.userPrizeGear === undefined || tournamentInfo.userPrizeGear === null) &&
                          tournamentData && tournamentInfo.userFinalPlacement) {
                        const placementStr = String(tournamentInfo.userFinalPlacement);
                        const currentPrizeTable = tournamentData.current_prize_table;
                        const configPrizeTable = tournamentData.config?.prize_table || {};
                        
                        // Get user's prize from current_prize_table (same as tournament tile)
                        const userPrize = currentPrizeTable?.[placementStr] || configPrizeTable[placementStr] || {};
                        if (userPrize.glint !== undefined) {
                          tournamentInfo.userPrizeGlint = userPrize.glint;
                        }
                        if (userPrize.gear !== undefined) {
                          tournamentInfo.userPrizeGear = userPrize.gear;
                        }
                        
                        console.log('[Game Complete] Fallback: Set prize info from current_prize_table:', {
                          placement: placementStr,
                          userPrizeGlint: tournamentInfo.userPrizeGlint,
                          userPrizeGear: tournamentInfo.userPrizeGear
                        });
                      }
                      sessionStorage.removeItem('tournament_result');
                    }
                  }
                  // Clear it after reading
                  sessionStorage.removeItem('current_tournament');
                
                } else {
                  // No tournament info in sessionStorage, but check tournament_result as fallback
                  const tournamentResultStr = sessionStorage.getItem('tournament_result');
                  if (tournamentResultStr) {
                    const tournamentResult = JSON.parse(tournamentResultStr);
                    // TIEBREAKER LOGIC: Check if this is a tournament match that needs a tiebreaker
                    if (tournamentResult?.tiebreaker_game_id) {
                      const matchId = tournamentResult.tiebreaker_match_id || tournamentInfo?.matchId;
                      const tournamentId = tournamentResult.tiebreaker_tournament_id || tournamentInfo?.tournamentId;
                      if (matchId && tournamentId) {
                        nextMatchInfo = {
                          id: matchId,
                          tournament_id: tournamentId,
                          gid: tournamentResult.tiebreaker_game_id,
                          is_tiebreaker: true,
                        };
                        console.log('[Game Complete] Using tiebreaker info from tournament_result (no stored tournament):', nextMatchInfo);
                      }
                    }
                    // Check if tournament is completed
                    else if (tournamentResult?.advancement?.completed || tournamentResult?.tournament_completed) {
                      // Tournament is completed - winner should see GameOverScreenV2
                      console.log('[Game Complete] Tournament completed (no stored tournament), winner should see GameOverScreenV2');
                      tournamentInfo = tournamentInfo || {};
                      tournamentInfo.tournamentCompleted = true;
                      tournamentInfo.userEliminated = false; // Winner is not eliminated
                      nextMatchInfo = null; // No next match
                    } else if (tournamentResult?.next_match) {
                      nextMatchInfo = tournamentResult.next_match;
                      if (tournamentResult?.next_match_gid) {
                        nextMatchInfo.gid = tournamentResult.next_match_gid;
                      }
                    }
                    if (tournamentResult?.user_eliminated) {
                      tournamentInfo = tournamentInfo || {};
                      tournamentInfo.userEliminated = true;
                    }
                    // Get placement from tournament_result
                    if (tournamentResult?.user_final_placement !== undefined) {
                      tournamentInfo = tournamentInfo || {};
                      tournamentInfo.userFinalPlacement = tournamentResult.user_final_placement;
                    }
                    // For this fallback path, we need to fetch tournament data if we don't have it
                    // But since we're in a fallback path without tournamentData, we'll skip prize calculation
                    // The user should still see the game over screen, just without prize info
                    // (This is a rare edge case - normally tournamentData would be available)
                    console.log('[Game Complete] Fallback path - no tournamentData available for prize calculation');

                    sessionStorage.removeItem('tournament_result');
                  }
                }
              } catch {}

              // Navigate to game over route with state - use updated balance from database
              navigate("/game/over", {
                state: {
                  outcome,
                  playerUsername: playerDisplayName,
                  opponentUsername: opponentDisplayName,
                  yourScore: state.scores.you,
                  opponentScore: state.scores.opp,
                  wager: wager,
                  currentBalance: updatedBalance, // Use balance fetched from database after completion
                  gameState: state, // Pass frozen game state to show board
                  tournamentInfo: tournamentInfo, // Pass tournament info if this was a tournament match
                  nextMatchInfo: nextMatchInfo, // Next match info if tournament continues
                },
              });
            }, delay);

          }
        } catch (err) {
          console.error('Failed to complete game:', err);
        }
      };
      
      handleGameComplete();
    }
      }, [state?.finished, completeGame, gameCompleted, currentWager, state?.scores, opponentDisplayName, playerDisplayName, navigate, refresh, refreshAchievements]);
    
  const updateLevelScrollIndicators = (target: HTMLDivElement | null) => {
    if (!target) return;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
    setShowLevelTopIndicator(!isAtTop);
    setShowLevelBottomIndicator(!isAtBottom);
  };

  useEffect(() => {
    const el = levelScrollRef.current;
    if (!el) return;
    const raf = window.requestAnimationFrame(() => updateLevelScrollIndicators(el));
    return () => window.cancelAnimationFrame(raf);
  }, [levelFrameMounted, levelCoverTx, scrollRect, levelsLoading, availableLevels]);

  if (loading) return <div className="p-6">Loading…</div>;
  // Allow rendering even without account - username modal will handle it

 // Unverified users can play - email verification is only required after 7 games  
  // Don't show "Connecting..." - show level selector instead
  // if (!state) return <div className="p-6">Connecting…</div>;

  // Hide navbar during active game or matchmaking flow (searching, match found, video ad)
  const shouldHideNavbar = !!state || isSearching || showMatchFound || showVideoAd;
  const coverScale = levelCoverTx?.scale || 1;
  const rowAspectRatio = rowArtSize ? rowArtSize.width / rowArtSize.height : 4.5;
  const viewportGap = 8 * coverScale;
  const viewportPadding = 10 * coverScale;

  // Fallback play-stamp rect, in case the mask no longer contains a separate play region.
  // These coordinates were measured from the previous 1920x1080 mask where the play area
  // existed as its own opaque rect.
  const effectivePlayRect = playRect || (levelArtSize ? {
    x: (1387 / 1920) * levelArtSize.width,
    y: (792 / 1080) * levelArtSize.height,
    width: (213 / 1920) * levelArtSize.width,
    height: (112 / 1080) * levelArtSize.height,
  } : null);
  
  // Debug logging for levelViewport calculation (disabled to reduce console spam)
  // if (typeof window !== 'undefined' && (window as any).Capacitor) {
  //   if (!levelCoverTx) {
  //     console.log("[levelViewport] levelCoverTx is null, levelArtSize:", levelArtSize);
  //   }
  //   if (!scrollRect) {
  //     console.log("[levelViewport] scrollRect is null");
  //   }
  //   if (levelCoverTx && scrollRect) {
  //     console.log("[levelViewport] Both levelCoverTx and scrollRect are set, computing viewport");
  //   }
  // }
  
  const levelViewport = levelCoverTx && scrollRect ? {
    left: levelCoverTx.offsetX + scrollRect.x * levelCoverTx.scale,
    top: levelCoverTx.offsetY + scrollRect.y * levelCoverTx.scale,
    width: scrollRect.width * levelCoverTx.scale,
    height: scrollRect.height * levelCoverTx.scale,
  } : null;
  const androidLevelMenuOffset = isAndroidTablet && isMobileLandscape ? ANDROID_TABLET_MENU_WIDTH : 0;
  const levelScrollHitPadX = usesAndroidPhoneLayout ? 140 * coverScale : 0;
  const androidLevelContentShift = isAndroidTablet && isMobileLandscape ? 8 : 0;
  const playViewport = levelCoverTx && effectivePlayRect ? {
    left: levelCoverTx.offsetX + effectivePlayRect.x * levelCoverTx.scale + playOffsetX - playGrow.left,
    top: levelCoverTx.offsetY + effectivePlayRect.y * levelCoverTx.scale,
    width: effectivePlayRect.width * levelCoverTx.scale + playGrow.left + playGrow.right,
    height: effectivePlayRect.height * levelCoverTx.scale,
  } : null;
  // Stamp tuning
  const playStampBaseScale = 2.852 * 1.15 * 0.99 * 0.98 * 0.99 * 0.99 * 0.99 * 0.98;
  const playStampScale = playStampBaseScale * (isMobile ? 0.99 : 1) * (usesAndroidPhoneLayout ? 0.98 * 0.98 : 1);
  const playStampMobileForwardX = 0;
  const playStampAndroidBackX = usesAndroidPhoneLayout ? 4 : 0;
  const playStampAndroidTabletBackX = 0; // Reset to original position
  const playStampAndroidTabletUpY = isAndroidTablet && isMobileLandscape ? -25 : 0; // Move play button up on Android tablets (adjusted down 15px total)
  const playStampLift = isIOSPad ? -35 * coverScale : 0; // Only lift on iPad
  const playStampIPhoneDownY = isIPhone ? 12 : 0; // Move play button down on iPhone
  const playStampViewport = playViewport ? {
    left: playViewport.left - (playViewport.width * (playStampScale - 1)) / 2 - 7 - (playViewport.width * playStampScale * 0.02) + 6 + playStampMobileForwardX + playStampAndroidBackX + playStampAndroidTabletBackX,
    top: playViewport.top - (playViewport.height * (playStampScale - 1)) / 2 - 35 - 3 + ((isMobile || isAndroid) ? 9 : 0) + playStampAndroidTabletUpY + playStampLift + playStampIPhoneDownY,
    width: playViewport.width * playStampScale,
    height: playViewport.height * playStampScale,
  } : null;
  const playStampStyle = playStampViewport ? {
    position: "absolute" as const,
    left: playStampViewport.left,
    top: playStampViewport.top,
    width: playStampViewport.width,
    height: playStampViewport.height,
    pointerEvents: "auto" as const,
    zIndex: 50,
  } : null;
  const handlePlayButtonClick = () => {
    if (!selectedWager) {
      setGameError('Select a level to play');
      return;
    }
    setGameError(null);
    if (startMatchRef.current) {
      startMatchRef.current();
    } else {
      setGameError('Matchmaking is not ready. Please try again.');
    }
  };

  const isPointerInsidePlayStamp = (e: React.PointerEvent, el: HTMLButtonElement | null) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
  };

  const clearPlayPressed = () => {
    setPlayPressed(false);
    playPointerIdRef.current = null;
  };

  const handlePlayPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    if (!selectedWager) return;
    if (playPointerIdRef.current !== null) return;
    const el = e.currentTarget;
    playPointerIdRef.current = e.pointerId;
    setPlayPressed(true);
    try {
      el.setPointerCapture(e.pointerId);
    } catch {}
  };

  const handlePlayPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (playPointerIdRef.current !== e.pointerId) return;
    if (!selectedWager) return;
    const inside = isPointerInsidePlayStamp(e, playStampButtonRef.current);
    setPlayPressed(inside);
  };

  const handlePlayPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (playPointerIdRef.current !== e.pointerId) return;
    const inside = isPointerInsidePlayStamp(e, playStampButtonRef.current);
    clearPlayPressed();

    if (inside && selectedWager) {
      suppressPlayClickRef.current = true;
      handlePlayButtonClick();
      window.setTimeout(() => {
        suppressPlayClickRef.current = false;
      }, 0);
    }
  };

  const handlePlayPointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (playPointerIdRef.current !== e.pointerId) return;
    clearPlayPressed();
  };

  const handleShowOutOfGear = () => {
    navigate("/", { state: { showOutOfGearModal: true } });
  };

  const handleShowLimitReached = () => {
    setShowLimitReachedModal(true);
  };

  const handleShowUsernameModal = () => {
    setShowUsernameModal(true);
  };

  const handleShowVideoAd = () => {
    console.log('[VideoAd] Showing ad before matchmaking');
    videoAdCompletedRef.current = false;
    setShowVideoAd(true);
  };

  const handleSetMatchmakingStart = (fn: () => Promise<void>) => {
    setPendingMatchmakingStart(() => fn);
  };

  const handleStartPve = async (w: number) => { 
    setGameCompleted(false);
    setCurrentWager(w); // Store wager for game over screen

    // Unverified users can now use matchmaking (removed guest restriction)

    // First, predict the opponent (without creating game) for registered users
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      // Check for regular user JWT token first (all platforms - JWT is primary)
      const regularUserToken = getRegularUserToken();
      if (regularUserToken) {
        headers['Authorization'] = `Bearer ${regularUserToken}`;
      } else {
        // Fallback to guest token
        try {
          const { getValidGuestToken } = await import('./lib/guest-auth');
          const guestToken = await getValidGuestToken();
          if (guestToken) {
            headers['Authorization'] = `Bearer ${guestToken}`;
          }
        } catch {
          // Continue without token
        }
      }
      
      const predictRes = await fetch(`${API_BASE}/matchmaking/predict-opponent`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ wager: w })
      });
      if (!predictRes.ok) throw new Error('Failed to predict opponent');
      const predictData = await predictRes.json();
      
      const opponentName = predictData.opponent_username || 'Lump';
      const opponentId = predictData.opponent_id;
      
      // Store game creation function to call after screen closes
      const gameStartFn = async () => {
        try {
          await newMatchedGame({ matchType:'pve', wager:w, opponentId: opponentId });
        } catch (err: any) {
          const message = err?.details || err?.message || 'Failed to start game';
          if (typeof message === 'string' && message.includes('INSUFFICIENT_GEAR')) {
            handleShowOutOfGear();
          } else if (typeof message === 'string' && message.includes('INSUFFICIENT_BALANCE')) {
            setGameError('Insufficient Glint for this level');
          } else {
            setGameError(message);
          }
          throw err;
        }
      };
      setPendingGameStart(() => gameStartFn);
      
      // Show match found screen with the predicted opponent name
      setMatchInfo({
        opponentUsername: opponentName,
        wager: w,
      });
      setShowMatchFound(true);
    } catch (err) {
      console.error('[Play] Error predicting opponent:', err);
      setGameError(err instanceof Error ? err.message : 'Failed to predict opponent');
    }
  };

  const handleStartPvp = async (w: number, oppId: number, oppName?: string, gid?: string) => {
    setGameCompleted(false);
    setCurrentWager(w); // Store wager for game over screen

    // Store game creation to run after the ad completes
    // For regular games with ads, NEVER pass gid - always create fresh game after ad
    // This ensures the timer starts fresh with full 20 seconds
    // Tournaments skip ads, so they handle gid differently
    const gameStartFn = async () => {
      const opponentName = oppName || 'Opponent';
      try {
        // For regular games (with ads), don't pass gid - create new game with fresh timer
        // This ensures the timer doesn't start until after the ad completes
        await newMatchedGame({ 
          matchType:'pvp', 
          wager:w, 
          opponentId:oppId, 
          opponentUsername:opponentName, 
          gid: undefined // Never use gid for regular games - always create fresh
        });
      } catch (err: any) {
        const message = err?.details || err?.message || 'Failed to start game';
        if (typeof message === 'string' && message.includes('INSUFFICIENT_GEAR')) {
          handleShowOutOfGear();
        } else if (typeof message === 'string' && message.includes('INSUFFICIENT_BALANCE')) {
          setGameError('Insufficient Glint for this level');
        } else {
          setGameError(message);
        }
        throw err;
      }
    };

    setPendingGameStart(() => gameStartFn);
    setMatchInfo({
      opponentUsername: oppName || 'Opponent',
      wager: w,
    });
    setShowMatchFound(true);
  };

  const handleRejoinGame = async (gid: string, matchType: 'pvp' | 'pve', wager: number, opponentUsername?: string) => {
    setGameCompleted(false);
    setCurrentWager(wager);
    
    // Rejoin existing game - call newMatchedGame directly without match found screen
    // This skips the match found screen and loads the game immediately with correct clock
    await newMatchedGame({ 
      matchType, 
      wager, 
      opponentUsername, 
      gid 
    });
  };

  const matchmakingControlProps = {
    apiBase: API_BASE,
    selectedWager,
    onError: setGameError,
    account,
    availableLevels,
    onSearchingChange: setIsSearching,
    onShowOutOfGear: handleShowOutOfGear,
    onShowLimitReached: handleShowLimitReached,
    onShowUsernameModal: handleShowUsernameModal,
    onShowVideoAd: handleShowVideoAd,
    onSetMatchmakingStart: handleSetMatchmakingStart,
    onStartPve: handleStartPve,
    onStartPvp: handleStartPvp,
    onRejoinGame: handleRejoinGame,
  };

  const totalGearCharges = (availableLevels?.gear_charges || 0) + (availableLevels?.purchased_gear_charges || 0) + (availableLevels?.tournament_gear_charges || 0);
  const tournamentGearCharges = availableLevels?.tournament_gear_charges || 0;

  const renderLevelRows = () => {
    if (levelsLoading) {
      return (
        <div className="text-center font-semibold text-[#2d1b0f]" style={{ padding: `${12 * coverScale}px` }}>
          Loading available levels...
        </div>
      );
    }

    if (!availableLevels) {
      if (!account) {
        return (
          <div className="text-center text-[#2d1b0f] font-semibold" style={{ padding: `${12 * coverScale}px` }}>
            Please set your username to continue
          </div>
        );
      }
      return (
        <div className="text-center text-red-700 font-semibold" style={{ padding: `${12 * coverScale}px` }}>
          Failed to load levels
        </div>
      );
    }

    if (parseFloat(availableLevels.balance) === 0 && totalGearCharges === 0) {
      return (
        <div className="text-center" style={{ padding: `${12 * coverScale}px`, color: '#3e2615' }}>
          <div className="font-bold mb-1">No Glint or Gear</div>
          <div className="text-sm opacity-80">You need ✧0.01 Glint or gear to start playing.</div>
          <div className="text-xs mt-1 opacity-70">
            Current: ✧{availableLevels.balance} Glint
            {(availableLevels.gear_charges || 0) > 0 && (
              <> · Free Gear: {availableLevels.gear_charges}</>
            )}
            {(tournamentGearCharges || 0) > 0 && (
              <> · Tournament Gear: {tournamentGearCharges}</>
            )}
            {(availableLevels.purchased_gear_charges || 0) > 0 && (
              <> · Paid Gear: {availableLevels.purchased_gear_charges}</>
            )}
          </div>
        </div>
      );
    }

    const levelsToShow = availableLevels.levels
      .filter((level: any, index: number) => {
        const lastAvailableIndex = availableLevels.levels
          .map((l: any) => l.available)
          .lastIndexOf(true);
        return index <= lastAvailableIndex + 3;
      });

    return (
      <div className="flex flex-col" style={{ gap: `${Math.max(6, viewportGap)}px` }}>
            {levelsToShow.map((level: any) => {
              const wagerValue = parseFloat(level.wager);
              const isAvailable = level.available;
              const isSelected = selectedWager === wagerValue;
              const rowBg = isAvailable ? getAssetPath("LevelSelectRow.png") : getAssetPath("LevelSelectRowGrey.png");
              const levelNumber = Math.round(Math.log2(wagerValue / 0.01) + 1);
              
              // Calculate responsive text scaling when width <= 875px
              const containerWidth = viewport.width || (typeof window !== 'undefined' ? window.innerWidth : 1000);
              const breakpointWidth = 875;
              const minScale = isIOSPad ? 0.85 : 0.75; // Keep higher floor only on iPad
              const widthScale = containerWidth <= breakpointWidth 
                ? Math.max(minScale, containerWidth / breakpointWidth)
                : 1;
              
              // Stack vertically when width <= 875px
              const shouldStack = containerWidth <= breakpointWidth;
              
              const baseFontSize = Math.max(12, Math.min(18, 12 + (coverScale - 1) * 6));
              const baseBadgeSize = Math.max(12, Math.min(18, 14 + (coverScale - 1) * 6));
              const levelFontScale = isIPhone ? 0.9 : 1; // Slightly smaller on iPhone
              const fontSize = baseFontSize * widthScale * (isAndroidTablet && isMobileLandscape ? 1.15 : 1) * (isIOSPad ? 1.1 : 1) * levelFontScale; // Slightly larger on iPad only
              const badgeSizeBase = baseBadgeSize * widthScale * (isIOSPad ? 1.1 : 1);
              const badgeSize = isAndroidTablet ? badgeSizeBase * 1.3 : badgeSizeBase; // Slightly increase badge size for Android tablets
              const pillSize = Math.max(11, fontSize - 1);
              
              // Calculate responsive spacing for flexbox layout
              const minRightDistance = usesAndroidPhoneLayout 
                ? 28 * coverScale 
                : isMobileLandscape 
                  ? 20 * coverScale 
                  : 14 * coverScale;
              
              const uniformGap = usesAndroidPhoneLayout 
                ? 35 * coverScale 
                : isMobileLandscape 
                  ? 32 * coverScale 
                  : 30 * coverScale;
              
              // Calculate max width to prevent overlap with LEVEL text
              const levelTextWidth = (badgeSize + 2) * 2.2 * coverScale;
              const androidLevelOffset = usesAndroidPhoneLayout ? (badgeSize + 2) * 0.9 * coverScale : 0;
              const paddingBuffer = 60 * coverScale;
              const maxContentWidth = `calc(100% - ${levelTextWidth}px - ${androidLevelOffset}px - ${paddingBuffer}px)`;
              const messageOffsetBase = usesAndroidPhoneLayout 
                ? 25 * coverScale 
                : isMobileLandscape 
                  ? 22 * coverScale 
                  : 20 * coverScale;
              const glintLeftBias = isIOSPad && level.available && !level.requires_gear ? -(messageOffsetBase * 3.2) : 0;
              const tabletLandscapeBias = isAndroidTablet && isMobileLandscape ? -50 : 0;

          return (
            <label
              key={level.wager}
              className="relative flex flex-col justify-center select-none transition-all"
              style={{
                width: '100%',
                aspectRatio: `${rowAspectRatio}`,
                backgroundImage: `url(${rowBg})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                padding: `${10 * coverScale}px ${16 * coverScale}px`,
                borderRadius: `${10 * coverScale}px`,
                border: '1px solid transparent',
                transform: isSelected ? `translateY(${-2 * coverScale}px)` : 'none',
                filter: isSelected ? `drop-shadow(0 ${10 * coverScale}px ${16 * coverScale}px rgba(0,0,0,0.28))` : 'none',
                opacity: isAvailable ? 1 : 0.65,
                cursor: isAvailable ? 'pointer' : 'not-allowed',
              }}
            >
              {isAvailable && isSelected && (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(125deg, rgba(255,245,210,0) 0%, rgba(255,245,210,0.22) 26%, rgba(255,245,210,0.42) 52%, rgba(255,245,210,0.24) 74%, rgba(255,245,210,0) 100%)',
                    mixBlendMode: 'screen',
                    opacity: 1,
                    WebkitMaskImage: `url(${rowMaskPath})`,
                    maskImage: `url(${rowMaskPath})`,
                    WebkitMaskSize: '100% 100%',
                    maskSize: '100% 100%',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    borderRadius: `${10 * coverScale}px`,
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )}
              <div className="flex items-center relative" style={{ gap: `${10 * coverScale}px`, color: '#2d1b0f' }}>
                <div className="flex items-center flex-shrink-0" style={{ gap: `${8 * coverScale}px` }}>
                  <input
                    type="radio"
                    name="wager"
                    value={level.wager}
                    checked={isSelected}
                    onChange={() => {
                      if (isAvailable) {
                        setSelectedWager(wagerValue);
                        // Haptic feedback on level selection
                        import('./lib/haptics').then(({ hapticLight }) => hapticLight()).catch(() => {});
                      }
                    }}
                    disabled={!isAvailable}
                    className="sr-only"
                  />
                  <div className="flex flex-col leading-tight flex-shrink-0" style={{ 
                    paddingLeft: `${(badgeSize + 2) * 2.2 - (isIPhone ? 10 * coverScale : 0)}px`,
                    ...(usesAndroidPhoneLayout ? { marginLeft: `${-((badgeSize + 2) * 0.9 * widthScale)}px` } : {}),
                    ...(isAndroidTablet && isMobileLandscape ? { marginLeft: `${-((badgeSize + 2) * 0.9 * widthScale) + 8 + 20}px` } : (isAndroidTablet ? { marginLeft: `${-((badgeSize + 2) * 0.9 * widthScale) + 8}px` } : {})),
                    ...(isIOSPad ? { marginLeft: `${-((badgeSize + 2) * 0.05 * widthScale)}px` } : {}),
                    ...(isIPhone ? { marginLeft: `${-((badgeSize + 2) * 1.1 * widthScale)}px` } : {})
                  }}>
                    <span className="font-black uppercase tracking-wide" style={{ 
                      fontSize: `${(isAndroidTablet && isMobileLandscape ? (badgeSize + 2) * 1.05 : (usesAndroidPhoneLayout ? (badgeSize + 2) * 0.9 : badgeSize + 2)) * (isIOSPad ? 1.12 : 1) * levelFontScale}px`, // Keep Android tablet bump and slightly lift on iPad; slightly smaller on iPhone
                      letterSpacing: '0.04em', 
                      color: '#EAD2A3',
                      whiteSpace: 'nowrap',
                      ...(isIOSPad ? { transform: `translateX(${18 * coverScale}px)` } : {})
                    }}>
                      Level {levelNumber}
                    </span>
                  </div>
                </div>
                
                {/* When there's a message (Gear Required or Insufficient Glint), use flexbox container */}
                {((level.available && level.requires_gear) || !level.available) ? (
                    <div className="absolute" style={{
                      left: '50%',
                      top: shouldStack ? '40%' : '50%',
                      transform: `translate(calc(-50% + ${messageOffsetBase}px), -50%)`,
                      display: 'flex',
                    alignItems: shouldStack ? 'center' : 'center',
                    justifyContent: shouldStack ? 'center' : 'flex-start',
                    gap: shouldStack ? `${uniformGap * 0.15}px` : `${uniformGap}px`,
                    flexDirection: shouldStack ? 'column-reverse' : 'row-reverse',
                    maxWidth: maxContentWidth,
                    minWidth: 'fit-content',
                    paddingLeft: `${minRightDistance}px`,
                    paddingRight: `${minRightDistance}px`,
                    zIndex: 3
                    }}>
                      {/* Message */}
                      {level.available && level.requires_gear && (
                        <span className="font-semibold whitespace-nowrap" style={{
                          fontSize: shouldStack ? `${pillSize * 0.85}px` : `${pillSize}px`,
                          ...(isAndroidTablet && isMobileLandscape && !shouldStack ? { fontSize: `${pillSize * 1.15}px` } : {}), // Slightly increase message text size for Android tablets
                        background: 'rgba(255, 209, 128, 0.9)',
                        padding: shouldStack ? `${3 * coverScale}px ${6 * coverScale}px` : `${4 * coverScale}px ${8 * coverScale}px`,
                        borderRadius: `${999 * coverScale}px`,
                        boxShadow: `0 ${1 * coverScale}px ${6 * coverScale}px rgba(0,0,0,0.2)`,
                        color: '#8b6542',
                        ...(shouldStack && { display: 'block', textAlign: 'center', width: '100%', lineHeight: 1.2 })
                      }}>
                        ⚙️ Gear Required ({totalGearCharges} remaining)
                      </span>
                    )}
                    {!level.available && (
                      <span className="font-semibold whitespace-nowrap" style={{
                        fontSize: shouldStack ? `${pillSize * 0.85}px` : `${pillSize}px`,
                        ...(isAndroidTablet && isMobileLandscape && !shouldStack ? { fontSize: `${pillSize * 1.15}px` } : {}), // Slightly increase message text size for Android tablets
                        color: '#ecb44c',
                        fontWeight: 600,
                        ...(shouldStack && { display: 'block', textAlign: 'center', width: '100%', lineHeight: 1.2 })
                      }}>
                        Insufficient Glint
                      </span>
                    )}
                    
                    {/* Glint number - variable width, handled by flexbox */}
                    <span className={`${isSelected ? 'font-bold' : 'font-semibold'}`} style={{
                      fontSize: shouldStack ? `${(fontSize + 6) * 0.85}px` : `${fontSize + 6}px`,
                      ...(isAndroidTablet && isMobileLandscape && !shouldStack ? { fontSize: `${(fontSize + 6) * 1.15}px` } : {}), // Slightly increase Glint text size for Android tablets
                      color: isSelected ? '#341a08' : (level.available ? (isIPhone ? '#3e2615' : '#9a6b3a') : '#ecb44c'),
                      whiteSpace: 'nowrap',
                      ...(shouldStack && { display: 'block', textAlign: 'center', width: '100%', lineHeight: 1.2 })
                    }}>
                      <span style={{ color: isSelected ? '#341a08' : (level.available ? '#9a6b3a' : '#ecb44c') }}>✧</span>{level.wager} Glint
                    </span>
                  </div>
                ) : (
                  /* When no message, center the Glint number normally with reduced offset (15% of message offset) */
                  <div className="flex-1 flex justify-center relative z-[3]" style={{
                    transform: `translateX(${(usesAndroidPhoneLayout ? 25 * coverScale * 0.15 : isMobileLandscape ? 22 * coverScale * 0.15 : 20 * coverScale * 0.15) + tabletLandscapeBias + glintLeftBias + (isIPhone ? 40 : 0)}px)`, // Move Glint text right on iPhone when no gear message
                    ...(shouldStack && {
                      left: '50%',
                      top: '40%',
                      position: 'absolute',
                      transform: `translate(calc(-50% + ${usesAndroidPhoneLayout ? 25 * coverScale : isMobileLandscape ? 22 * coverScale : 20 * coverScale}px${isAndroidTablet && isMobileLandscape ? ' - 50px' : ''}${isIOSPad && glintLeftBias ? ` + ${glintLeftBias}px` : ''}${isIPhone ? ` + 40px` : ''}), -50%)` // Move Glint text right on iPhone when no gear message
                    })
                  }}>
                    <span className={`${isSelected ? 'font-bold' : 'font-semibold'}`} style={{
                      fontSize: `${fontSize + 6}px`,
                      ...(isAndroidTablet && isMobileLandscape ? { fontSize: `${(fontSize + 6) * 1.15}px` } : {}), // Slightly increase Glint text size for Android tablets
                      color: isSelected ? '#341a08' : (isIPhone ? '#3e2615' : '#9a6b3a')
                    }}>
                      <span style={{ color: isSelected ? '#341a08' : (isIPhone ? '#3e2615' : '#9a6b3a') }}>✧</span>{level.wager} Glint
                    </span>
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    );
  };

  const renderLegacyMobileLevelSelect = () => {
    return (
      <div className="px-2 md:px-6 pb-6">
        <div 
          className="relative p-6 rounded-2xl border-4 shadow-xl" 
          style={{ 
            background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
            borderColor: '#8b6542'
          }}
        >
          <h3 className="font-bold text-2xl mb-4" style={{ color: '#3e2615' }}>Select Wager Level</h3>
          {levelsLoading ? (
            <div className="text-center py-4">Loading available levels...</div>
          ) : availableLevels ? (
            <div className="space-y-3">
              {parseFloat(availableLevels.balance) === 0 && totalGearCharges === 0 ? (
                <div className="text-center py-6">
                  <div className="font-semibold text-gray-800 mb-2 text-lg">No Glint or Gear</div>
                  <div className="text-gray-600 mb-4">
                    You need ✧0.01 Glint or gear to start playing. Purchase gear to enter the mine.
                  </div>
                  <div className="text-gray-500 text-sm">
                    Current: ✧{availableLevels.balance} Glint
                    {(availableLevels.gear_charges || 0) > 0 && (
                      <>, Free Gear: {availableLevels.gear_charges}</>
                    )}
                    {(tournamentGearCharges || 0) > 0 && (
                      <>, Tournament Gear: {tournamentGearCharges}</>
                    )}
                    {(availableLevels.purchased_gear_charges || 0) > 0 && (
                      <>, Paid Gear: {availableLevels.purchased_gear_charges}</>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid gap-2 max-h-[500px] overflow-y-auto pr-2 pb-20">
                  {availableLevels.levels
                    .filter((level: any, index: number) => {
                      const lastAvailableIndex = availableLevels.levels
                        .map((l: any) => l.available)
                        .lastIndexOf(true);
                      return index <= lastAvailableIndex + 3;
                    })
                    .map((level: any) => (
                  <label
                    key={level.wager}
                    className={`flex ${isPortrait ? 'flex-col' : 'flex-row items-center'} ${isPortrait ? 'p-4' : 'p-5'} rounded-xl border-2 cursor-pointer transition-all ${
                    level.available 
                      ? selectedWager === parseFloat(level.wager)
                        ? ''
                        : ''
                      : 'cursor-not-allowed opacity-50'
                  } gap-1 ${isPortrait ? '' : 'gap-3'}`}
                    style={{
                      borderColor: level.available 
                        ? selectedWager === parseFloat(level.wager) ? '#3e2615' : '#8b6542'
                        : '#c4a07a',
                      background: level.available
                        ? selectedWager === parseFloat(level.wager) ? '#fffaeb' : 'rgba(255, 250, 235, 0.7)'
                        : 'rgba(255, 250, 235, 0.3)',
                      boxShadow: selectedWager === parseFloat(level.wager) ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
                    }}
                  >

                    {isPortrait ? (
                      <>
                        <div className="flex items-center gap-2 text-[#3e2615]">
                          <input
                            type="radio"
                            name="wager"
                            value={level.wager}
                            checked={selectedWager === parseFloat(level.wager)}
                            onChange={() => {
                              setSelectedWager(parseFloat(level.wager));
                              // Haptic feedback on level selection
                              import('./lib/haptics').then(({ hapticLight }) => hapticLight()).catch(() => {});
                            }}
                            disabled={!level.available}
                            className="mr-1"
                          />
                          <span className="bg-[#3e2615] text-white font-semibold rounded-full whitespace-nowrap text-sm px-3 py-1">
                            Level {Math.round(Math.log2(parseFloat(level.wager) / 0.01) + 1)}
                          </span>
                          <span className="font-bold whitespace-nowrap">
                            ✧{level.wager} Glint
                          </span>
                        </div>
                        {!level.available && (
                          <div className="text-gray-600 text-sm ml-6">
                            Insufficient Glint (need ✧{level.wager})
                          </div>
                        )}
                        {level.available && level.requires_gear && (
                          <div className="bg-orange-100 text-orange-800 rounded whitespace-nowrap text-xs px-2 py-1 ml-6">
                            ⚙️ Gear Required ({totalGearCharges} remaining)
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          type="radio"
                          name="wager"
                          value={level.wager}
                          checked={selectedWager === parseFloat(level.wager)}
                          onChange={() => {
                            setSelectedWager(parseFloat(level.wager));
                            // Haptic feedback on level selection
                            import('./lib/haptics').then(({ hapticLight }) => hapticLight()).catch(() => {});
                          }}
                          disabled={!level.available}
                          className="mr-2 md:mr-3"
                        />
                        <div className="flex flex-wrap items-center text-[#3e2615] flex-1 gap-2 md:gap-3">
                          <span className="bg-[#3e2615] text-white font-semibold rounded-full whitespace-nowrap text-base px-4 py-1.5">
                            Level {Math.round(Math.log2(parseFloat(level.wager) / 0.01) + 1)}
                          </span>
                          <span className="font-bold whitespace-nowrap text-base">
                            ✧{level.wager} Glint
                          </span>
                          {!level.available && (
                            <span className="text-gray-600 text-sm">
                              Insufficient Glint (need ✧{level.wager})
                            </span>
                          )}
                          {level.available && level.requires_gear && (
                            <span className="bg-orange-100 text-orange-800 rounded whitespace-nowrap text-xs px-2 py-1">
                              ⚙️ Gear Required ({totalGearCharges} remaining)
                            </span>
                          )}
                        </div>
                      </>
                    )}

                  </label>
                ))}
                </div>
              )}

              {gameError && (
                <div className="bg-red-50 border border-red-200 rounded-lg text-red-800 flex-shrink-0 mt-4 p-3">
                  <div className="font-medium text-sm">Cannot start game</div>
                  <div className="text-sm">{gameError}</div>
                </div>
              )}
            </div>
          ) : !account ? (
            <div className="text-center py-4 text-gray-600">Please set your username to continue</div>
          ) : (
            <div className="text-center py-4 text-red-600">Failed to load levels</div>
          )}
        </div>

        {gameError && (
          <div className="bg-red-50 border border-red-200 rounded-lg text-red-800 mt-3">
            <div className="font-medium px-3 pt-3">Cannot start game</div>
            <div className="px-3 pb-3 text-sm">{gameError}</div>
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 z-50 px-2 md:px-6 pb-4" style={{ background: 'linear-gradient(to top, rgba(247, 178, 103, 0.98) 0%, rgba(247, 178, 103, 0.95) 50%, transparent 100%)' }}>
          <MatchmakingControls
            {...matchmakingControlProps}
          />
        </div>
      </div>
    );
  };
  
  return (
    <PageLayout account={account} onLogout={handleLogout} hideNavbar={shouldHideNavbar}>
      {/* Solid backdrop for mobile landscape to prevent any bleed-through/ghosting behind the level art */}
      {isMobileLandscape && (
        <div className="fixed inset-0 bg-black" style={{ zIndex: 0 }} aria-hidden="true" />
      )}
      {/* Level Selection - Don't show for tournament matches */}
      {!state && !searchParams.get('tournament') && !searchParams.get('gid') && !sessionStorage.getItem('tournament_match_starting') && !sessionStorage.getItem('current_tournament') && (
        useLegacyMobilePortrait ? (
          renderLegacyMobileLevelSelect()
        ) : (
          <div className={isMobileLandscape ? "" : "px-2 md:px-6 pb-6"}>
            <div
              ref={(el) => {
                levelFrameRef.current = el;
                setLevelFrameMounted(!!el);
              }}
              className={isMobileLandscape ? "fixed inset-0 w-screen h-screen overflow-hidden z-10" : "relative w-full rounded-2xl border-4 overflow-hidden shadow-xl md:max-h-[95vh]"}
              style={isMobileLandscape ? {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: '#000',
                borderColor: '#8b6542'
              } : {
                borderColor: '#8b6542',
                background: '#0b0b0b',
                aspectRatio: '16/9',
                maxWidth: '100%'
              }}
            >
              {levelCoverTx && levelArtSize ? (
                <img
                  src={getAssetPath("LevelSelect.png")}
                  alt="Level Select"
                  style={{
                    position: 'absolute',
                    ...((isAndroidTablet && isMobileLandscape) || (isIOSPad && isMobileLandscape) ? {
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    } : {
                      left: levelCoverTx.offsetX,
                      top: levelCoverTx.offsetY,
                      width: levelArtSize.width * levelCoverTx.scale,
                      height: levelArtSize.height * levelCoverTx.scale,
                      objectFit: 'cover',
                      objectPosition: 'center'
                    })
                  }}
                  draggable={false}
                />
              ) : (
                <div
                  className="absolute inset-0"
                  style={{ background: '#000' }}
                  aria-hidden="true"
                />
              )}

              {levelViewport ? (
                <div
                  className="absolute"
                  style={{
                    left: levelViewport.left - levelScrollHitPadX - androidLevelMenuOffset + androidLevelContentShift + (isAndroidTablet && isMobileLandscape ? 75 : 0), // Move level rows right on Android tablets
                    top: levelViewport.top,
                    width: levelViewport.width + levelScrollHitPadX * 2 + androidLevelMenuOffset + androidLevelContentShift,
                    height: levelViewport.height,
                    overflow: 'hidden',
                    pointerEvents: 'auto'
                  }}
                >
                  <style>{`.level-scroll::-webkit-scrollbar{display:none;}`}</style>
                  <div
                    className="w-full h-full level-scroll"
                    ref={levelScrollRef}
                    onScroll={(e) => updateLevelScrollIndicators(e.currentTarget)}
                    style={{
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                  >
                    <div
                      style={{
                        width: `${levelViewport.width}px`,
                        marginLeft: `${levelScrollHitPadX}px`,
                        padding: `${viewportPadding}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${viewportGap}px`,
                      }}
                    >
                      <div className="flex-1 min-h-0" style={{ paddingRight: `${6 * coverScale}px` }}>
                        {renderLevelRows()}
                      </div>
                    </div>
                  </div>
                  {/* Scroll indicators (match navbar caret style) */}
                  {showLevelTopIndicator && (
                    <div
                      className="absolute flex justify-center"
                      style={{
                        top: `${4 * coverScale}px`,
                        left: `${levelScrollHitPadX + levelViewport.width / 2}px`,
                        transform: 'translateX(-50%)',
                        zIndex: 40,
                        pointerEvents: 'none',
                      }}
                    >
                      <svg
                        width={28 * coverScale}
                        height={18 * coverScale}
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))', opacity: 1 }}
                      >
                        <path d="M1 7L6 2L11 7" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                  {showLevelBottomIndicator && (
                    <div
                      className="absolute flex justify-center"
                      style={{
                        bottom: `${4 * coverScale}px`,
                        left: `${levelScrollHitPadX + levelViewport.width / 2}px`,
                        transform: 'translateX(-50%)',
                        zIndex: 40,
                        pointerEvents: 'none',
                      }}
                    >
                      <svg
                        width={28 * coverScale}
                        height={18 * coverScale}
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))', opacity: 1 }}
                      >
                        <path d="M1 1L6 6L11 1" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white font-semibold">
                  Preparing layout…
                </div>
              )}

              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 55%, rgba(0,0,0,0.45) 100%)' }}
              />

              {playViewport && (
                <button
                  ref={playStampButtonRef}
                  type="button"
                  aria-label="Play"
                  disabled={!selectedWager}
                  className={`play-stamp-button absolute${playPressed ? " is-pressed" : ""}`}
                  style={playStampStyle || undefined}
                  onClick={(e) => {
                    if (suppressPlayClickRef.current) {
                      e.preventDefault();
                      return;
                    }
                    handlePlayButtonClick();
                  }}
                  onPointerDown={handlePlayPointerDown}
                  onPointerMove={handlePlayPointerMove}
                  onPointerUp={handlePlayPointerUp}
                  onPointerCancel={handlePlayPointerCancel}
                  onLostPointerCapture={() => clearPlayPressed()}
                  onPointerLeave={() => {
                    if (playPressed) setPlayPressed(false);
                  }}
                >
                  <img
                    src={getAssetPath("PlayStamp.png")}
                    alt=""
                    draggable={false}
                    className="play-stamp-img"
                  />
                </button>
              )}

              {isMobileLandscape && gameError && (
                <div
                  className="absolute left-0 right-0"
                  style={{
                    bottom: `calc(env(safe-area-inset-bottom, 8px) + ${84 * coverScale}px)`,
                    padding: '0 16px',
                    zIndex: 30
                  }}
                >
                  <div className="bg-red-50 border border-red-200 rounded-lg text-red-800 shadow-lg" style={{ padding: `${10 * coverScale}px` }}>
                    <div className="font-medium" style={{ fontSize: `${12 + coverScale * 2}px` }}>Cannot start game</div>
                    <div className="text-sm">{gameError}</div>
                  </div>
                </div>
              )}

            </div>

            {!isMobileLandscape && gameError && (
              <div className="bg-red-50 border border-red-200 rounded-lg text-red-800 mt-3">
                <div className="font-medium px-3 pt-3">Cannot start game</div>
                <div className="px-3 pb-3 text-sm">{gameError}</div>
              </div>
            )}

          </div>
        )
      )}

      {!useLegacyMobilePortrait && (
        <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          <MatchmakingControls 
            {...matchmakingControlProps}
            renderButton={false}
            onExposeStart={(fn) => { startMatchRef.current = fn; }}
          />
        </div>
      )}

      {/* Match Found Screen - overlays everything - keep visible until gameboard is ready */}
      {showMatchFound && matchInfo && (
        <div 
          className="fixed inset-0 z-[100]"
          style={{
            display: 'grid',
            alignItems: overlayAlignItems,
            justifyItems: 'center',
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            padding: overlayPadding,
            overflowY: 'auto'
          }}
        >
          <div 
            style={{ 
              width: '100%',
              maxWidth: isMobile ? '640px' : '880px'
            }} 
            className="flex justify-center"
          >
            <MatchFoundScreen
              opponentUsername={matchInfo.opponentUsername}
              wager={matchInfo.wager}
              onComplete={handleMatchScreenComplete}
            />
          </div>
        </div>
      )}

      {/* Video Ad Modal - shows after match found screen for regular games */}
      <VideoAdModal
        isOpen={showVideoAd}
        onComplete={handleVideoAdComplete}
        onError={handleVideoAdError}
      />

      {/* Game board - only render if ad is not showing (prevents game from starting behind ad) */}
      {state && account && !showVideoAd && (
      
        <div
          className={
            isMobile && isPortrait
              ? "w-full mt-3"
              : isMobile && !isPortrait
              ? "fixed inset-0 p-0"
              : isFullscreen
              ? "fixed inset-0 p-0"
              : "relative w-full px-2 md:px-6"
          }
          style={{
            ...(isMobile && isPortrait
              ? {}
              : isMobile && !isPortrait
              ? {
                  position: 'fixed',
                  width: '100vw',
                  height: '100dvh',
                  zIndex: 1,
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  margin: 0,
                  padding: 0
                }
              : isFullscreen
              ? {
                  position: 'fixed',
                  width: '100vw',
                  height: '100vh',
                  zIndex: 9999,
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  margin: 0,
                  padding: 0,
                  backgroundColor: '#f7e3c2'
                }
              : {})
          }}
        >
          <div
            className={
              isMobile && isPortrait
                ? "w-full overflow-hidden bg-[#f7e3c2] rounded-2xl shadow"
                : isMobile && !isPortrait
                ? "w-full h-full overflow-hidden bg-[#f7e3c2] relative"
                : isFullscreen
                ? "w-full h-full overflow-hidden bg-[#f7e3c2] relative"
                : "w-full overflow-hidden bg-[#f7e3c2] rounded-3xl shadow aspect-video relative"
            }
            style={{
              display: 'block',
              ...(isMobile && isPortrait
                ? { width: '100%', aspectRatio: '16 / 9' }
                : isMobile && !isPortrait
                ? {
                    height: '100%',
                    width: '100%'
                  }
                : isFullscreen
                ? {
                    height: '100%',
                    width: '100%'
                  }
                : { width: '100%', maxWidth: '100%' })
            }}
          >
            {/* Fullscreen button - desktop only */}
            {!isMobile && (
              <button
                onClick={toggleFullscreen}
                className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-lg transition-all"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
            )}
            <BoardSVG
              s={state}
              onPlay={handlePlay}
              onNextRound={nextRound}
              playerUsername={playerDisplayName}
              opponentUsername={opponentDisplayName}
              isFullscreen={isFullscreen && !isMobile}
            />
          </div>
        </div>
      )}
      {state && account && isMobile && isPortrait && (
        <div className="text-center text-sm text-[#3e2615] mt-3 leading-tight px-4">
          <div className="font-semibold uppercase">Please rotate your device</div>
          <div>Goldrush is best played in landscape mode.</div>
        </div>
      )}

      <LimitReachedModal
        isOpen={showLimitReachedModal}
        onClose={() => {
          setShowLimitReachedModal(false);
        }}
        onCompleteRegistration={async () => {
          setShowLimitReachedModal(false);
          await refresh(); // Refresh account after completing registration
        }}
        apiBase={API_BASE}
        hasEmail={account?.email ? !account.email.endsWith('@guest.local') : false}
        allowClose={true}
        title="Continue Playing"
      />

      <UsernameSelectionModal
        isOpen={showUsernameModal}
        onClose={() => {
          // Don't allow closing - user must set username
        }}
        onSuccess={async (username) => {
          setShowUsernameModal(false);
          await refresh(); // Refresh account to get updated username
        }}
        apiBase={API_BASE}
      />

    </PageLayout>
  );
}

// Lightweight matchmaking UI component embedded inside Play
function MatchmakingControls({ apiBase, selectedWager, onError, onStartPve, onStartPvp, account, availableLevels, onShowOutOfGear, onShowLimitReached, onShowUsernameModal, onRejoinGame, onShowVideoAd, onSetMatchmakingStart, isCompact, onSearchingChange, renderButton = true, onExposeStart }: {
  apiBase: string;
  selectedWager: number | null;
  onError: (msg: string | null)=>void;
  onStartPve: (w: number)=>Promise<void>;
  onStartPvp: (w: number, opponentId: number, opponentName?: string, gid?: string, tournamentMatchId?: number)=>Promise<void>;
  account?: Account | null;
  availableLevels?: any;
  onShowOutOfGear?: () => void;
  onShowLimitReached?: () => void;
  onShowUsernameModal?: () => void;
  onRejoinGame?: (gid: string, matchType: 'pvp' | 'pve', wager: number, opponentUsername?: string)=>Promise<void>;
  onShowVideoAd?: () => void;
  onSetMatchmakingStart?: (fn: () => Promise<void>) => void;
  isCompact?: boolean;
  onSearchingChange?: (isSearching: boolean) => void;
  renderButton?: boolean;
  onExposeStart?: (startFn: () => void) => void;
}) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobile = viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOS = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'ios';
  const isNativeApp = isAndroid || isIOS;
  const overlayAlignItems = isNativeApp ? 'center' : 'start';
  const overlayPadding = isNativeApp ? '24px 12px' : (isMobile ? '80px 12px 40px' : '140px 16px 64px');

  const [searching, setSearching] = useState(false);
  const [keepOverlayVisible, setKeepOverlayVisible] = useState(false);
  const [timer, setTimer] = useState(15);
  const [busy, setBusy] = useState(false);
  const cancelledRef = useRef(false);
  
  // Notify parent when searching state changes
  useEffect(() => {
    if (onSearchingChange) {
      onSearchingChange(searching);
    }
  }, [searching, onSearchingChange]);
  const matchmakingLockKey = 'matchmaking_lock_v1';
  const searchTokenRef = useRef<string | null>(null);
  const [showTournamentWarning, setShowTournamentWarning] = useState(false);
  const [pendingTournament, setPendingTournament] = useState<{ name: string; scheduledStartTime: string; id: number; userEntryStatus: string } | null>(null);
  const [dismissedTournamentIds, setDismissedTournamentIds] = useState<Set<number>>(new Set());
  const [showRemoveAdsModal, setShowRemoveAdsModal] = useState(false);
  const adGateActiveRef = useRef(false);
  const pendingPostAdStartRef = useRef<(() => Promise<void>) | null>(null);
  const navigate = useNavigate();

  const clearMatchmakingLock = useCallback(() => {
    try { localStorage.removeItem(matchmakingLockKey); } catch {}
  }, [matchmakingLockKey]);

  // Calculate level from wager (Level 1 = 0.01, Level 2 = 0.02, Level 3 = 0.04, etc.)
  const level = useMemo(() => {
    if (!selectedWager) return null;
    return Math.round(Math.log2(selectedWager / 0.01) + 1);
  }, [selectedWager]);  

  // Audio refs for sound management
  const waitingAudioRef = useRef<HTMLAudioElement | null>(null);
  const matchFoundAudioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio objects once
  useEffect(() => {
    const waitingAudio = new Audio('./WaitingForMatch.mp3');
    waitingAudio.loop = true; // Loop the waiting sound
    waitingAudio.preload = 'auto';
    
    const matchFoundAudio = new Audio('./MatchFound.mp3');
    matchFoundAudio.preload = 'auto';
    
    // Add error listeners to help debug
    waitingAudio.addEventListener('error', (e) => {
      console.error('WaitingForMatch audio error:', e, waitingAudio.error);
      console.error('Audio src:', waitingAudio.src);
      console.error('Can play MP3?', waitingAudio.canPlayType('audio/mpeg'));
    });
    
    matchFoundAudio.addEventListener('error', (e) => {
      console.error('MatchFound audio error:', e, matchFoundAudio.error);
      console.error('Audio src:', matchFoundAudio.src);
      console.error('Can play MP3?', matchFoundAudio.canPlayType('audio/mpeg'));
    });
    
    // Try to load and verify they can load
    waitingAudio.load();
    matchFoundAudio.load();
    
    waitingAudioRef.current = waitingAudio;
    matchFoundAudioRef.current = matchFoundAudio;
    
    // Cleanup on unmount
    return () => {
      if (waitingAudioRef.current) {
        waitingAudioRef.current.pause();
        waitingAudioRef.current = null;
      }
      if (matchFoundAudioRef.current) {
        matchFoundAudioRef.current.pause();
        matchFoundAudioRef.current = null;
      }
    };
  }, []);

  // Generic helper function to play audio safely (waits for load if needed)
  const playAudioSafe = useCallback(async (audioRef: React.RefObject<HTMLAudioElement>, soundName: string) => {
    if (!shouldPlaySound()) return;
    if (!audioRef.current) {
      console.error(`${soundName} audio ref is null!`);
      return;
    }
    
    const audio = audioRef.current;
    audio.currentTime = 0;
    
    // Wait for audio to be ready if needed
    if (audio.readyState < 2) { // HAVE_CURRENT_DATA
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Audio load timeout'));
        }, 3000);
        
        const canPlay = () => {
          clearTimeout(timeout);
          audio.removeEventListener('canplay', canPlay);
          audio.removeEventListener('error', onError);
          resolve();
        };
        
        const onError = (e: Event) => {
          clearTimeout(timeout);
          audio.removeEventListener('canplay', canPlay);
          audio.removeEventListener('error', onError);
          reject(e);
        };
        
        audio.addEventListener('canplay', canPlay);
        audio.addEventListener('error', onError);
        audio.load(); // Trigger load if not already loading
      }).catch(err => {
        console.error(`Error waiting for ${soundName} to load:`, err);
      });
    }
    
    try {
      await audio.play();
    } catch (err) {
      console.error(`Failed to play ${soundName}:`, err);
      console.error('Audio readyState:', audio.readyState);
      console.error('Audio src:', audio.src);
    }
  }, []);

  // Manage waiting sound based on searching state
  useEffect(() => {
    if (searching && waitingAudioRef.current) {
      playAudioSafe(waitingAudioRef, 'WaitingForMatch').catch(err => {
        console.error('Error playing waiting sound:', err);
      });
    } else if (waitingAudioRef.current) {
      waitingAudioRef.current.pause();
      waitingAudioRef.current.currentTime = 0; // Reset to start
    }
  }, [searching, playAudioSafe]);

  useEffect(()=>{ if(!searching) return; if (timer<=0) return; const t = setTimeout(()=>setTimer(t=>t-1), 1000); return ()=>clearTimeout(t); }, [searching, timer]);

  // Clear matchmaking lock on unmount so stale tabs don't block new searches
  useEffect(() => () => clearMatchmakingLock(), [clearMatchmakingLock]);

  // Helper function to check for upcoming tournaments
  const checkUpcomingTournaments = async (): Promise<{ name: string; scheduledStartTime: string; id: number; userEntryStatus: string } | null> => {
    try {
      // Check for regular user JWT token first (all platforms - JWT is primary)
      const headers: HeadersInit = { "Content-Type": "application/json" };
      let regularUserToken: string | null = null;
      if (typeof window !== 'undefined') {
        regularUserToken = localStorage.getItem('regular_user_jwt');
      }
      if (regularUserToken) {
        headers["Authorization"] = `Bearer ${regularUserToken}`;
      } else {
        // Fallback to guest token
        try {
          const { getValidGuestToken } = await import('./lib/guest-auth');
          const guestToken = await getValidGuestToken();
          if (guestToken) {
            headers["Authorization"] = `Bearer ${guestToken}`;
          }
        } catch {
          // Continue without token
        }
      }

      const response = await fetch(`${apiBase}/tournaments?category=active`, {
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const activeTournaments: any[] = data.tournaments || [];

      const now = Date.now();
      const WARNING_THRESHOLD_MS = 90 * 1000; // 90 seconds

      // Find tournaments where user has an entry
      // Include tournaments that are starting soon OR already in progress
      // Exclude tournaments where user is eliminated
      const soonTournaments = activeTournaments
        .filter((t: any) => {
          // Must have user entry and scheduled start time
          if (!t.user_entry || !t.scheduled_start_time) return false;
          // Exclude if user is eliminated or disqualified
          const entryStatus = t.user_entry.status;
          if (entryStatus === "eliminated" || entryStatus === "disqualified") return false;
          return true;
        })
        .map((t: any) => {
          const startMs = new Date(t.scheduled_start_time).getTime();
          const timeUntilStart = startMs - now;
          const isInProgress = t.status === "in_progress";
          
          return {
            ...t,
            startMs,
            timeUntilStart,
            isInProgress,
          };
        })
        .filter((t: any) => {
          // Include if:
          // 1. Tournament is in progress (already started) - user can still play matches
          // 2. Tournament starts within the warning threshold (not started yet)
          return t.isInProgress || (t.timeUntilStart > 0 && t.timeUntilStart <= WARNING_THRESHOLD_MS);
        })
        .sort((a: any, b: any) => {
          // Sort in-progress tournaments first, then by start time
          if (a.isInProgress !== b.isInProgress) {
            return a.isInProgress ? -1 : 1;
          }
          return a.startMs - b.startMs;
        });

      if (soonTournaments.length === 0) {
        return null;
      }

      const next = soonTournaments[0];

      // Check if user already dismissed this tournament
      if (dismissedTournamentIds.has(next.id)) {
        return null;
      }

      return {
        name: next.name,
        scheduledStartTime: next.scheduled_start_time,
        id: next.id,
        userEntryStatus: next.user_entry.status,
      };
    } catch (error) {
      console.error('[TournamentWarning] Error checking upcoming tournaments:', error);
      return null;
    }
  };

  const waitForForeground = useCallback(async () => {
    if (typeof document === "undefined") {
      return;
    }
    if (document.visibilityState === "visible") {
      return;
    }

    await new Promise<void>((resolve) => {
      let resolved = false;
      let removeAppListener: (() => void) | null = null;

      const finish = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve();
      };

      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          finish();
        }
      };

      const handleFocus = () => {
        finish();
      };

      const cap = typeof window !== "undefined" ? (window as any).Capacitor : null;
      const App = cap?.Plugins?.App;
      if (App?.addListener) {
        const listener = App.addListener("appStateChange", (state: { isActive: boolean }) => {
          if (state.isActive) {
            finish();
          }
        });
        removeAppListener = () => listener.remove?.();
      }

      const cleanup = () => {
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("focus", handleFocus);
        if (removeAppListener) {
          removeAppListener();
        }
      };

      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("focus", handleFocus);
    });
  }, []);

  const startAfterAd = useCallback(async () => {
    await waitForForeground();
    await new Promise((resolve) => setTimeout(resolve, 50));
    await startInternal();
  }, [waitForForeground, startInternal]);

    // Check gear for unverified users before starting matchmaking
    // Gear is only needed when balance <= 0.01 AND wager == 0.01
    if (account && !account.email_verified) {
      const balance = parseFloat(availableLevels?.balance || '0');
      const totalGear = (availableLevels?.gear_charges || 0) + (availableLevels?.purchased_gear_charges || 0) + (availableLevels?.tournament_gear_charges || 0);
      if (balance <= 0.01 && totalGear === 0 && selectedWager === 0.01) {
        // Unverified user is out of gear and can't afford the wager - show modal instead of matchmaking
        if (onShowOutOfGear) {
          onShowOutOfGear();
        }
        return;
      }
    }


  // Internal start function that does the actual matchmaking (used after tournament check)
  async function startInternal() {
    if (!selectedWager || busy) return;
    if (isNativeApp && (isUnityAdShowing() || adGateActiveRef.current || showRemoveAdsModal)) {
      return;
    }

    const selectedLevel = availableLevels?.levels?.find((lvl: any) => parseFloat(lvl.wager) === selectedWager);
    const totalGear = (availableLevels?.gear_charges || 0) + (availableLevels?.purchased_gear_charges || 0) + (availableLevels?.tournament_gear_charges || 0);
    if (selectedLevel && !selectedLevel.available) {
      if (selectedLevel.requires_gear) {
        onShowOutOfGear?.();
      } else {
        onError('Insufficient Glint for this level');
      }
      return;
    }
    if (selectedLevel?.requires_gear && totalGear <= 0) {
      onShowOutOfGear?.();
      return;
    }
    if (selectedLevel && !selectedLevel.available && selectedLevel.requires_gear) {
      onShowOutOfGear?.();
      return;
    }

    // Clear any previous errors when starting matchmaking
    onError(null);

    // Prevent starting a second search in another tab for the same user
    try {
      const raw = localStorage.getItem(matchmakingLockKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed?.startedAt || 0);
        const isSameUser = parsed?.userId && account?.id && parsed.userId === account.id;
        if (isSameUser && age < 45000) {
          onError('You are already in matchmaking in another tab. Finish or cancel that search first.');
          return;
        }
      }
    } catch {}

    // Check if user needs to set username before starting
    if (account && !account.email_verified && account.username &&
        (account.username.startsWith('Guest_') || account.username.startsWith('guest_'))) {
      // Show username modal
      if (onShowUsernameModal) {
        onShowUsernameModal();
      }
      return;
    }

    // Check if user has hit the 7-game limit before starting matchmaking
    if (account && !account.email_verified && account.unverified_games_played !== undefined && 
        account.unverified_games_played >= 7) {
      // Show limit reached modal - don't start matchmaking or audio
      if (onShowLimitReached) {
        onShowLimitReached();
      }
      return;
    }

    setBusy(true);
    onError(null);
    setSearching(true);
    setTimer(15);
    cancelledRef.current = false; // Reset cancel flag
    searchTokenRef.current = (crypto as any)?.randomUUID?.() || `search-${Date.now()}-${Math.random()}`;

    // Mark matchmaking active for this user across tabs
    try {
      localStorage.setItem(matchmakingLockKey, JSON.stringify({
        userId: account?.id || 'guest',
        wager: selectedWager,
        startedAt: Date.now(),
      }));
    } catch {}

    const poll = async (remaining: number) => {
      // Check if search was cancelled
      if (cancelledRef.current) {
        return;
      }
      
      try {
        // Use api() function which includes JWT token for guests
        const data = await api<{status: string; game_id?: string; gid?: string; opponent_username?: string; opponent_id?: number; is_reentry?: boolean; match_type?: string}>("/matchmaking/search", {
          method: 'POST',
          body: JSON.stringify({ wager: selectedWager, search_token: searchTokenRef.current })
        });

        if (data.status === 'matched') {
          // Stop waiting sound first
          if (waitingAudioRef.current) {
            waitingAudioRef.current.pause();
            waitingAudioRef.current.currentTime = 0;
          }
          // Don't set searching to false yet - keep overlay visible until match found screen appears
          // setSearching(false); setBusy(false);
          clearMatchmakingLock();
          searchTokenRef.current = null;
          
          // If this is a reentry, skip the match found screen and load game directly
          if (data.is_reentry && data.gid && typeof onRejoinGame === 'function') {
            // Reentry: load game directly without match found screen
            setSearching(false); setBusy(false);
            const matchType = (data.match_type || 'pve') as 'pvp' | 'pve';
            await onRejoinGame(data.gid, matchType, selectedWager!, data.opponent_username);
            return;
          }
          
          // New match: show match found screen
          
          // Play match found sound and wait briefly for it to start
          playAudioSafe(matchFoundAudioRef, 'MatchFound').catch(err => {
            console.error('[Match] Error playing match found sound:', err);
          });

          // Brief delay to let sound start playing before state changes
          await new Promise(resolve => setTimeout(resolve, 200));

          if (typeof onStartPvp !== 'function') {
            throw new Error('Match start failed: onStartPvp is not available');
          }
           // Show match found screen first, keep searching overlay visible during transition
           setKeepOverlayVisible(true); // Keep overlay visible even after searching ends
           await onStartPvp(selectedWager!, data.opponent_id, data.opponent_username, data.gid);
           // Wait for match found screen to fully render
           await new Promise(resolve => setTimeout(resolve, 500));
           setSearching(false); 
           setBusy(false);
           // Keep overlay visible a bit longer to ensure smooth transition
           setTimeout(() => setKeepOverlayVisible(false), 200);
           return;
        }

        if (remaining <= 1) {
          // cancel and fallback to AI

          // Stop waiting sound first
          if (waitingAudioRef.current) {
            waitingAudioRef.current.pause();
            waitingAudioRef.current.currentTime = 0;
          }

          playAudioSafe(matchFoundAudioRef, 'MatchFound').catch(err => {
            console.error('[AI Match] Error playing match found sound:', err);
          });
          
          // Brief delay to let sound start playing before state changes
          await new Promise(resolve => setTimeout(resolve, 200));

          try { await api("/matchmaking/cancel", { method:'POST', body: JSON.stringify({ wager: selectedWager }) }); } catch {}
          setSearching(false); setBusy(false);
          clearMatchmakingLock();
          searchTokenRef.current = null;
          if (typeof onStartPve !== 'function') {
            throw new Error('Match start failed: onStartPve is not available');
          }
          await onStartPve(selectedWager!);
          return;
        }

        setTimeout(()=>poll(remaining-1), 1000);
      } catch (err: any) {
        // Check for limit reached error
        const errorText = err?.message || '';
        if (errorText.includes('UNVERIFIED_LIMIT_REACHED') || errorText.includes('7 guest games')) {
          if (onShowLimitReached) {
            onShowLimitReached();
          }
          setSearching(false);
          setBusy(false);
          clearMatchmakingLock();
          return;
        }
        if (errorText.includes('ALREADY_SEARCHING') || errorText.includes('ALREADY_IN_GAME')) {
          onError('You are already in matchmaking in another tab. Finish or cancel that search first.');
          setSearching(false);
          setBusy(false);
          clearMatchmakingLock();
          return;
        }
        onError(err?.message || 'Failed to start matchmaking');
        setSearching(false); setBusy(false);
        clearMatchmakingLock();
      }
    };

    poll(15);
  }

  async function start() {
    if (!selectedWager || busy) return;
    if (isNativeApp && (isUnityAdShowing() || adGateActiveRef.current || showRemoveAdsModal)) {
      return;
    }
    
    // Haptic feedback on play button tap
    import('./lib/haptics').then(({ hapticLight }) => hapticLight()).catch(() => {});

    // Check for upcoming tournaments before starting
    const upcomingTournament = await checkUpcomingTournaments();
    if (upcomingTournament) {
      setPendingTournament(upcomingTournament);
      setShowTournamentWarning(true);
      return; // Wait for user to confirm or cancel
    }

    if (isNativeApp) {
      const adResult = await maybeShowAdBeforeMatchmaking();

      // Only start matchmaking after ad completes (or if ad wasn't shown)
      // If ad was shown, wait for it to complete before starting
      if (adResult.shown) {
        adGateActiveRef.current = true;
        pendingPostAdStartRef.current = startAfterAd;
        setShowRemoveAdsModal(true);
      } else if (adResult.reason === "already_showing" || adResult.reason === "busy") {
        // Ad is already in progress (double tap / duplicate start); don't start matchmaking.
        return;
      } else {
        // Ad wasn't shown (not due, not ready, etc.), start matchmaking normally
        await startInternal();
      }
      return;
    }

    // Track matchmaking attempts and show ad every third time
    const AD_FREQUENCY = 3;
    let matchmakingCount = 0;
    try {
      const stored = localStorage.getItem('matchmaking_count');
      matchmakingCount = stored ? parseInt(stored, 10) : 0;
    } catch (e) {
      console.warn('[Matchmaking] Failed to read matchmaking count from localStorage:', e);
    }
    
    // Increment count
    matchmakingCount += 1;
    try {
      localStorage.setItem('matchmaking_count', matchmakingCount.toString());
    } catch (e) {
      console.warn('[Matchmaking] Failed to save matchmaking count to localStorage:', e);
    }

    // Show video ad every third time (when count is divisible by 3) - only if ads are enabled
    const shouldShowAd = ENABLE_ADS && matchmakingCount % AD_FREQUENCY === 0;
    
    if (shouldShowAd && onShowVideoAd && onSetMatchmakingStart) {

      // Store the matchmaking start function so parent can call it after ad completes
      onSetMatchmakingStart(startInternal);
      // Show the ad modal
      onShowVideoAd();
    } else {
      // Fallback: start matchmaking directly if ad handler not available or not time for ad
      await startInternal();
    }
  }

  useEffect(() => {
    if (onExposeStart) {
      onExposeStart(start);
      return () => onExposeStart(() => {});
    }
  }, [start, onExposeStart]);

  const handleTournamentWarningConfirm = () => {
    setShowTournamentWarning(false);
    if (pendingTournament) {
      // Mark as dismissed for this session
      setDismissedTournamentIds(prev => new Set(prev).add(pendingTournament.id));
    }
    setPendingTournament(null);
    // Continue with game start (skip tournament check this time)
    startInternal();
  };

  const handleTournamentWarningCancel = () => {
    setShowTournamentWarning(false);
    setPendingTournament(null);
  };

  const handleViewTournament = () => {
    setShowTournamentWarning(false);
    setPendingTournament(null);
    navigate('/tournaments');
  };

  const handlePlayMatch = () => {
    setShowTournamentWarning(false);
    setPendingTournament(null);
    navigate('/tournaments');
  };

  async function cancel() {
    if (!selectedWager) return;
    cancelledRef.current = true; // Set cancel flag to stop polling
    try { await api("/matchmaking/cancel", { method:'POST', body: JSON.stringify({ wager: selectedWager }) }); } catch {}
    setSearching(false); setBusy(false);
    clearMatchmakingLock();
    searchTokenRef.current = null;
  }

  return (
    <>
      {renderButton && (
        <button
          onClick={start}
          disabled={!selectedWager || busy}
          className={`w-full rounded-xl font-bold transition-all shadow-lg ${isCompact ? 'py-2 px-3 text-sm' : 'py-4 px-4 text-lg'}`}
          style={{
            background: selectedWager && !busy 
              ? '#fffaeb'
              : 'rgba(255, 250, 235, 0.5)',
            color: selectedWager && !busy ? '#3e2615' : '#8b7a6a',
            boxShadow: selectedWager && !busy ? (isCompact ? '0 2px 0 #8b6542, 0 3px 6px rgba(0,0,0,0.3)' : '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)') : '0 2px 4px rgba(0,0,0,0.2)',
            border: '2px solid #8b6542',
            cursor: selectedWager && !busy ? 'pointer' : 'not-allowed'
          }}
          onMouseEnter={(e) => { if (selectedWager && !busy) e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={(e) => { if (selectedWager && !busy) e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {busy ? (searching ? `Searching… ${timer}s` : 'Starting…') : selectedWager ? `Start Game at ✧${selectedWager.toFixed(2)}` : 'Select a wager level'}
        </button>
      )}

      {(searching || keepOverlayVisible) && (
        <div 
          className="fixed inset-0 z-[100]"
          style={{
            display: 'grid',
            alignItems: overlayAlignItems,
            justifyItems: 'center',
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            padding: overlayPadding,
            overflowY: 'auto'
          }}
        >
          <div className="rounded-xl md:rounded-2xl w-full max-w-[320px] md:w-[400px] text-center border-3 md:border-4" style={{
            background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
            borderColor: '#8b6542',
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '1rem 0.75rem' : '2rem',
            gap: isMobile ? '0.5rem' : '1rem'
          }}>
            <div className="flex-shrink-0">
              <div className="animate-spin h-6 w-6 md:h-12 md:w-12 rounded-full border-3 md:border-4 mx-auto mb-2 md:mb-4" style={{
                borderColor: '#3e2615',
                borderTopColor: 'transparent'
              }} />
              <div className="text-sm md:text-2xl font-bold mb-1 md:mb-2" style={{ color: '#3e2615' }}>Searching for opponent…</div>
              <div className="text-xs md:text-lg font-semibold" style={{ color: '#3e2615' }}>
                Time remaining: {timer}s
              </div>
            </div>

            {selectedWager && level && (
              <div className="flex-shrink-0 space-y-1.5 md:space-y-2">
                <div className="inline-flex items-center justify-center px-3 py-1 md:px-4 md:py-2 rounded-full border-2" style={{
                  background: 'rgba(245, 214, 164, 0.6)',
                  borderColor: '#8b6542',
                  backdropFilter: 'blur(4px)'
                }}>
                  <span className="text-sm md:text-xl font-bold" style={{ color: '#3e2615' }}>Level {level}</span>
                </div>
                <div className="text-xs md:text-base font-semibold" style={{ color: '#3e2615' }}>
                  Total Glint wagered: <span className="text-sm md:text-xl font-bold" style={{ color: '#1a0f08' }}>✧{selectedWager.toFixed(2)}</span>
                </div>
              </div>
            )}
            
            <button 
              onClick={cancel} 
              className="text-xs md:text-base font-medium underline hover:no-underline transition-all py-1 md:py-2 mt-auto flex-shrink-0" 
              style={{ color: '#3e2615' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <TournamentWarningModal
        isOpen={showTournamentWarning}
        tournamentName={pendingTournament?.name || ''}
        scheduledStartTime={pendingTournament?.scheduledStartTime || ''}
        tournamentId={pendingTournament?.id || 0}
        userEntryStatus={pendingTournament?.userEntryStatus || ''}
        onConfirm={handleTournamentWarningConfirm}
        onCancel={handleTournamentWarningCancel}
        onViewTournament={handleViewTournament}
        onPlayMatch={handlePlayMatch}
        onEliminated={() => {
          setShowTournamentWarning(false);
          setPendingTournament(null);
        }}
      />
      <RemoveAdsModal
        isOpen={showRemoveAdsModal}
        variant="postAd"
        onContinue={async () => {
          setShowRemoveAdsModal(false);
          adGateActiveRef.current = false;
          const startFn = pendingPostAdStartRef.current || startAfterAd;
          pendingPostAdStartRef.current = null;
          await startFn();
        }}
        onClose={async () => {
          setShowRemoveAdsModal(false);
          adGateActiveRef.current = false;
          const startFn = pendingPostAdStartRef.current || startAfterAd;
          pendingPostAdStartRef.current = null;
          await startFn();
        }}
        onPurchaseSuccess={async () => {
          setAdsRemoved(true);
          setShowRemoveAdsModal(false);
          adGateActiveRef.current = false;
          pendingPostAdStartRef.current = null;
          // Ads removed, start matchmaking directly
          await startInternal();
        }}
      />
    </>
  );
}

export default function App() {
  // Use HashRouter on itch.io (subdirectory paths), BrowserRouter on regular sites
  const isItch = 
    window.location.hostname.endsWith('itch.io') ||
    window.location.hostname.endsWith('itch.zone');
  const Router = isItch ? HashRouter : BrowserRouter;

  useEffect(() => {
    void initPlayGames();
    void syncAdsRemovedFromBilling();
    
    // Listen for app state changes to handle profile/account switches
    // When app comes to foreground, reset sign-in state to re-authenticate with current account
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      const { App } = (window as any).Capacitor.Plugins;
      if (App && App.addListener) {
        App.addListener('appStateChange', (state: { isActive: boolean }) => {
          if (state.isActive) {
            // App came to foreground - reset sign-in to pick up account changes
            console.log('[PlayGames] App came to foreground, resetting sign-in state');
            resetSignInState();
            // Re-initialize to authenticate with current account
            void initPlayGames();
            void syncAdsRemovedFromBilling();
          }
        });
      }
    }
  }, []);

  return (
    <AchievementsProvider>
      <SidekickProvider>
        <Router>
          <AchievementsToastHost />
          <SidekickOverlay />
          <div className="app-background min-h-screen p-2 md:p-6" style={{
            overflowY: 'visible'
          }}>
            <Routes>
              <Route path="/" element={<Gate />} />
              <Route path="/login" element={<Login onSuccess={()=>location.assign("/")} />} />
              <Route path="/register" element={<Register onSuccess={()=>location.assign("/")} />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset-password" element={<Reset />} />
              <Route path="/play" element={<Play />} />
              <Route path="/game/over" element={<GameOverScreenV2 />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/achievements" element={<AchievementsPageWrapper />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/tournaments" element={<TournamentsPageWrapper />} />
              <Route path="/account" element={<AccountPageWrapper />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="*" element={<div className="p-8">Not found</div>} />
            </Routes>
          </div>
        </Router>
      </SidekickProvider>
    </AchievementsProvider>
  );
}
